<template>
  <div class="content">
    <div class="i2p-new-page" v-if="useHighLevelAllocation !== null">
      <ed-title-nav ref='header' :title="$t('Submit Invoice')" :subtitle="getSubtitle">
        <budget-scan-snapshot
          v-if='settingsModel.CompanySettings.EditingInvoiceScan'
          :model="budgetScanSnapshotModel"
          slot="overview"
        >
        </budget-scan-snapshot>
        <budget-snapshot
          v-else
          :model="budgetSnapshotModel"
          slot="overview"
        />
        <span slot="action">
          <a v-if="$root.legacySubtab && !showSubmitBatchBtn" href="javascript:;" v-on:click="beforeCancelInvoice">
            <i class="fa fa-long-arrow-left"></i>
            <span>&nbsp; Back</span>
          </a>
        </span>

        <!-- Action Buttons -->
        <TheToolbar slot="toolbar">
          <comments :invoiceKey="invoiceInformationModel.InvoiceKey"
                    :paymentInvoiceId="paymentInvoiceId"
                    :programId="programId"
                    :userToken="user.token"
                    v-if="showCommentsBtn"></comments>
          <ed-button v-if="invoiceInformationModel.IsFromEmailForScanning"
                    button-class="btn btn-actionbar"
                    @click="openReviewEmailModal()"
                    >
                    Review Email
          </ed-button>
          <ReviewEmailModal
            :show="showReviewEmailModal"
            :emailData="reviewEmailContent"
            :paymentInvoiceId="paymentInvoiceId"
            @close="closeReviewEmailModal"
          />
          <ed-button v-if="!checkZipMatches && settingsModel.CompanySettings.EditingInvoiceScan === true && isEvedSupplier"
                     button-class="btn btn-actionbar"
                     @click="sendZipMismatchEmail()"
                     :endpoint="api.$.invoice.sendZipMismatchEmail">
            Send Zip Mismatch Email
          </ed-button>
          <ed-button v-if="showSaveBtn"
                     name='save'
                     :disabled='submitDisabled'
                     button-class="btn btn-actionbar"
                     @click="saveOneInvoice(false)">
            Save As Draft
          </ed-button>

          <ed-button v-if="showAddAnotherBtn"
                     name='add-another'
                     :disabled='submitDisabled'
                     button-class="btn btn-actionbar"
                     @click="submitAndAddAnother()">
            Add Another Invoice
          </ed-button>

          <ed-button v-if="showSubmitBatchBtn"
                     name='submit-batch'
                     :disabled='submitDisabled'
                     button-class="btn btn-actionbar"
                     class="alt"
                     @click="initSubmitBatch()">
            Submit Batch
          </ed-button>

          <ed-button v-if="!showSubmitBatchBtn"
                     name='submit'
                     :disabled='submitDisabled'
                     button-class="btn btn-actionbar"
                     class="alt"
                     @click="saveOneInvoice(true)">
            Submit Invoice
          </ed-button>
        </TheToolbar>

      </ed-title-nav>

      <!-- Page -->
      <div class="container-fluid">
        <br>
        <invoice-information
          :model="invoiceInformationModel"
          :divisionLabel="divisionLabel"
          :purchaseOrderForBillTo="initData.purchaseOrder"
          :settings="settingsModel"
          :user="user"
          :useHighLevelAllocation="useHighLevelAllocation"
          :maxInvoiceAmount="maxInvoiceAmount"
          :initialInvoiceAmount="invoiceInformationInitialModel.InvoiceAmount"
          @supplierSelected="addSupplierData"
          @supplierRemoved="removeSupplierData"
          @currencySelected="onCurrencySelected"
          @beforeProgramSelected="validateProgramSelected"
          @changeSameDaySetting="onChangeSameDaySetting"
          @changeReimbursementSetting="onChangeReimbursementSetting"
          @discountSelected="onDiscountSelected"
          @updatePOModel="onUpdatePOModel"
          @poAmountChanged="onPOAmountChanged"
          @supplier-zip-code-changed="checkIfZipMatches"
          ref="invoiceInformationRef"
        />

        <div v-if="showLineItems">
          <line-items-section
            v-if="canAddLineItems"
            :model="purchaseOrder"
            :dependencies="dependencies"
            :settings="settingsModel.CompanySettings"
            :programId="programId"
            :invoiceId="invoiceInformationModel.PaymentInvoiceId"
            :buyerVendorId="user.buyerVendorId"
            :useSavingsFields="invoiceInformationModel.UseSavingsFields && (!isP2PInvoice || isP2PInvoiceWithAddEditItems)"
            :invoiceSettings="invoiceInformationModel"
            :isP2PInvoice="isP2PInvoice"
            :decouplingSettings="getDecouplingSettings"
            ref='lineItemsComp'
          >

          </line-items-section>
          <div v-else-if="useHighLevelAllocation">
            <high-allocation-section
              v-if="showCCAllocations"
              :model="ccAllocations"
              :currencyCode="currencyCode"
              :invoiceAmount='InvoiceAmount'
            />
            <high-allocation-section
              v-if="showPairsAllocations"
              :model="pairsAllocations"
              :currencyCode="currencyCode"
              :invoiceAmount='InvoiceAmount'
            />
            <high-allocation-section
              v-if="showCategoryAllocations"
              :model="categoriesAllocations"
              :currencyCode="currencyCode"
              :invoiceAmount='InvoiceAmount'
              :vatAmount='VATAmount'
              :additionalNoRequired='categRefIdRequired'
            />
          </div>
        </div>


        <div class="content no-flex active" v-if='settingsModel.CompanySettings.EditingInvoiceScan === false'>
          <ed-section class="no-flex active no-border" title="Approval" wide>
            <div class="col-md-12">
              <div class="col-md-8">
                <label>
                  <strong>Invoice Approval</strong>
                </label>
              </div>
              <ed-checkbox
                @click='checkApprovalCheckBox'
                :options='approvalCheckboxOptions'
                name='approve_invoice_checkbox'
                required
                :error='approvalCheckboxError'
                helptip-body="Please approve the submission of this invoice to continue"
                helptip-header=""
                class="col-md-8"
              >
              </ed-checkbox>

              <div v-if="isCreateInvoice() || invoiceInformationModel.IsDraft" class="col-md-8">
                <label><strong>{{settingsModel.CompanySettings.ApprovalNoteLabel}} <span v-if="!settingsModel.CompanySettings.IsApprovalNoteRequired">- optional</span></strong></label>
                <br />
                <span v-if="settingsModel.CompanySettings.ApprovalNotePlaceholder">
                  <small>
                    <em>Please complete the statement below</em>
                  </small>
                </span>
                <ed-textarea
                  v-model="invoiceInformationModel.ApproveNote"
                  :rows="5"
                  @input="onApprovalNoteChange"
                  :error='approvalNoteError'
                  errorTipBody="Please enter the Approval Note"
                  errorTipHeader=""
                  name='invoice_approval_note_textarea'>
                </ed-textarea>
              </div>
            </div>
          </ed-section>
        </div>

        <div class="content no-flex active" v-if='showAdditionalInvoiceConfirmation'>
          <ed-section class="no-flex active no-border no-margin" title="Additional Invoice Confirmation" wide>
            <div class="col-md-12">
              <div class="col-md-8">
                <label>
                  <strong>Additional Invoice Confirmation</strong>
                </label>
              </div>
              <ed-checkbox
                @click='checkAdditionalInvoiceConfirmationBox'
                :options='additionalInvoiceConfirmationOptions'
                name='additional_confirm_invoice_checkbox'
                required
                :error='additionalInvoiceConfirmationError'
                helptip-body="Please check the additional invoice confirmation"
                helptip-header=""
                class="col-md-8 line-break"
              >
              </ed-checkbox>
            </div>
          </ed-section>
        </div>

        <div class="content no-flex active">
          <ed-section class="no-flex active no-border no-margin" title=" " wide>
            <div class="col-md-12">
              <div class="col-md-5">
                <ed-button class="cta alt" v-if="showAddAnotherBtn"
                  :disabled='submitDisabled'
                  button-class='btn btn-secondary'
                  @click='submitAndAddAnother()'
                >
                  {{ $t("Add Another Invoice") }}
                </ed-button>
              </div>
              <div class="col-md-3" v-if="showSubmitBatchBtn">
                <ed-button class="cta alt"
                  :disabled='submitDisabled'
                  @click='initSubmitBatch()'
                >
                  {{ $t("Submit Batch") }}
                </ed-button>
              </div>
              <div :class="showAddAnotherBtn ? 'col-md-3' : 'col-md-8'" v-if="!showSubmitBatchBtn">
                <ed-button v-if="!checkZipMatches && settingsModel.CompanySettings.EditingInvoiceScan === true && isEvedSupplier"
                           class="cta alt"
                           style="background-color: white; color: #333; border: 1px solid #ccc;"
                           @click="sendZipMismatchEmail()"
                           :endpoint="api.$.invoice.sendZipMismatchEmail">
                  Send Zip Mismatch Email
                </ed-button>
                <ed-button class="cta alt"
                           :disabled='submitDisabled'
                           @click='saveOneInvoice(true)'>
                  {{ $t("Submit Invoice") }}
                </ed-button>
              </div>
            </div>
          </ed-section>
        </div>
      </div>

      <ed-modal
        :show='showValidationError'
        @close='closeValidationError'
        :title='validationTitle'
        primaryButton='false'
        secondaryButton='Ok'
        type='small'
      >
        <div slot="body">
          <p><br>{{ validationErrorTitle }}</p>
        </div>
      </ed-modal>

      <ed-modal
        :show='showDuplicateInvoiceStep1 && !showDuplicateInvoiceStep2'
        title='Duplicate Invoice Warning'
        primaryButton='Submit Invoice'
        secondaryButton='Cancel'
        @close='showDuplicateInvoiceStep1 = false'
        @confirm='showDuplicateInvoiceStep2 = true'
        :closeOnEsc="true"
      >
        <div slot="body">
          <br />
          <p class="small">It looks like the invoice you are submitting may be a duplicate.</p>
          <p>Click here to view the duplicate invoice PDF in a new tab.</p>
          <p v-for="(inv, index) in duplicateInvoices" :key="index"><a :href="duplicateInvoicePdfUrl(inv.PaymentInvoiceID)" target="_blank">{{inv.InvoiceNumber}}</a></p>
          <p class="small">Please review the potential existing duplicate invoice and then indicate whether you will want to submit this invoice for payment.</p>
          <br />
        </div>
      </ed-modal>

      <ed-modal
        :show='showDuplicateInvoiceStep2'
        title='Duplicate Invoice Warning - Confirm Submission'
        primaryButton='Submit Invoice'
        secondaryButton='Cancel'
        @close='showDuplicateInvoiceStep2 = false'
        @confirm='duplicateInvoiceAction'
        :closeOnEsc="true"
      >
        <div slot="body">
          <br />
          <p class="small">Are you sure you want to submit this duplicate invoice?</p>
          <br />
        </div>
      </ed-modal>

      <ed-modal
        :show='showOverBudgetWarning'
        title='Invoice Over Budget'
        primaryButton='OK'
        secondaryButton='EDIT'
        additionalButton='CANCEL INVOICE'
        @close='showOverBudgetWarning = false'
        @confirm='confirmOverBudget'
        @additionalAction='doCancelInvoiceAction'
        :closeOnEsc="true"
      >
        <div class="content form-group" slot="body">
          <br />
          <p>The total amount of the {{ overBudgetModel.Context.toLowerCase().trim() }} puts your {{programLabel}} over budget.</p><br />
          <div class="row small">
            <strong class="col-md-6">Revised Budget:</strong>
            <span class="col-md-6">
              {{ overBudgetModel.RevisedBudgetFormatted }}
              &nbsp;
            </span>

            <strong class="col-md-6">Current Budget Balance:</strong>
            <span class="col-md-6">
              {{ overBudgetModel.BalanceFormatted }}
              &nbsp;
            </span>

            <strong class="col-md-6">{{ overBudgetModel.Context }} Total:</strong>
            <span class="col-md-6">
              {{ overBudgetModel.POAmountFormatted }}
              &nbsp;
            </span>

            <strong class="col-md-6" v-if="overBudgetModel.ExcludeVATFromBudget">{{ overBudgetModel.Context }} Total less VAT/GST:</strong>
            <span class="col-md-6" v-if="overBudgetModel.ExcludeVATFromBudget">
              {{ overBudgetModel.POAmountLessVATFormatted }}
              &nbsp;
            </span>

            <strong class="col-md-6">Total Over Budget Amount:</strong>
            <span class="col-md-6">
              {{ overBudgetModel.POAmountOverBudgetFormatted }}
              &nbsp;
            </span>
          </div>
          <br />

          <p class="small">{{overBudgetModel.Message}}</p>
          <br />
        </div>
      </ed-modal>

      <ed-modal
        :show='showOverBudgetWarningBeforeSendingToScan'
        title='Invoice Over Budget'
        primaryButton='OK'
        secondaryButton='CANCEL'
        @confirm='onAcceptOverBudgetWarningBeforeSendingToScan'
        @close='showOverBudgetWarningBeforeSendingToScan = false'
        :closeOnEsc="true"
      >
        <div class="content form-group" slot="body">
          <p>The total amount of the invoice puts your {{programLabel}} over budget.</p><br />
          <div class="row small">
            <strong class="col-md-6">Total Over Budget Amount:</strong>
            <span class="col-md-6">
              <v-money :currency="invoiceInformationModel.ProgramCurrencyCode" name="invoice-amount-modal">{{ overBudgetAmountBeforeSendingToScan }}</v-money>
              &nbsp;
            </span>
          </div>
        </div>
      </ed-modal>

      <ed-modal
        :show='showSupplierZipCodeWarning'
        title='Postal/Zip Codes do not match'
        primaryButton='SUPPLIER IS CORRECT'
        secondaryButton='GO BACK'
        @confirm='onAcceptSupplierZipCodeWarning'
        @close='showSupplierZipCodeWarning = false'
        :closeOnEsc="true"
      >
        <div class="content form-group" slot="body">
          <p>The Postal/Zip Code you provided is different than the Supplier's Postal/Zip Code in Eved.</p><br />
          <p>Please confirm you have selected the correct supplier or click Go Back to return to the invoice submission.</p>
        </div>
      </ed-modal>

      <ed-modal
        :show='showPendingScanningWarning'
        title='Invoice Submitted'
        primaryButton='false'
        secondaryButton='Ok'
        @close='closePendingScanningModal'
        @confirm='closePendingScanningModal'
        :closeOnEsc="true"
      >
        <div class="content form-group" slot="body">
          <p>Your invoice has been successfully submitted and it will be scanned in 1 business day.</p>
        </div>
      </ed-modal>

      <ed-modal
        :show='showDifferentAmountWarning'
        title='Invoice Scanning'
        primaryButton='CONFIRM'
        secondaryButton='CANCEL'
        @close='onCloseDifferentAmountModal'
        @confirm='onConfirmDifferentAmountModal'
        :closeOnEsc="true"
      >
        <div class="content form-group" slot="body">
          <p>The total amount of the line items is <v-money :currency="currencyCode" name="invoice-amount-modal">{{ purchaseOrder.PO.total }}</v-money>.
          The planner indicated that the invoice needs to be <v-money :currency="currencyCode" name="initial-invoice-amount-modal">{{ invoiceInformationModel.InitialTotalAmount }}</v-money>. Are you sure the invoice is correct?</p>
        </div>
      </ed-modal>

      <ed-modal
        :show='showUnitPriceAtZeroWarning'
        title='Unit Price at Zero'
        primaryButton='Submit'
        secondaryButton='Go Back'
        @close='showUnitPriceAtZeroWarning = false'
        @confirm='onConfirmUnitPriceAtZero'
        :closeOnEsc="true"
      >
        <div class="content form-group" slot="body">
          <p>One or more line items in the invoice have been set as <v-money :currency="currencyCode" name="invoice-amount-modal">0</v-money>.
          If that was not intended, select Go Back and enter a new price.</p>
        </div>
      </ed-modal>

      <ed-modal
        :show='modals.showSubmitBatchConfirmation'
        title='Submit Batch'
        primaryButton='Submit'
        secondaryButton='Go Back'
        @show='$refs.batchInvoicesRef.onSelectAll(true)'
        @close='onSubmitBatchClose'
        @confirm='onSubmitBatchConfirmation'
        :closeOnEsc="true"
        type='medium'
      >
        <div class="content form-group" slot="body">
          <p>{{getBatchPopupMessage}}</p>
          <grid
            layout="frame"
            :class="{ simple: true }"
            :model="getBatchGridModel"
            :data="batch.invoices"
            :fit-columns="true"
            :auto-height="true"
            :disableFilter="true"
            :checkboxes="true"
            :hideSelectAll="true"
            ref="batchInvoicesRef"
            :search="false"
            :resizing="false"
            :footer="false"
            @selectedRows="onBatchInvoiceSelected"
          ></grid>
          <i>{{noOfInvoicesToSubmit}} invoices will be submitted, {{noOfDraftInvoicesInBatch}} saved as a draft</i>
        </div>
      </ed-modal>

      <ed-modal
        :show='showBudgetAlmostUsedNotification'
        :title="'Budget balance is less than 20% left'"
        primaryButton='false'
        secondaryButton='Ok'
        @close='closeBudgetAlmostUsedNotification'
        @confirm='closeBudgetAlmostUsedNotification'
        :closeOnEsc="true"
        type='medium'
      >
        <div class="content form-group" slot="body">
          <p>Submitting this invoice will bring the budget balance to less than 20%. Please review the {{programLabel}} and request additional budget if additional invoices need to be paid to your suppliers.</p>
        </div>
      </ed-modal>

      <ed-modal
        :show='modals.showSupplierProvideServicesConfirmation && purchaseOrder.PO.billTo.address'
        :title="'Did the supplier provide services in the ' + purchaseOrder.PO.billTo.address.country + '?'"
        :primaryButton="'Services provided inside the ' + purchaseOrder.PO.billTo.address.country"
        :secondaryButton="'Services provided outside the ' + purchaseOrder.PO.billTo.address.country"
        @close='closeSupplierProvideServicesConfirmation'
        @confirm='onSupplierProvideServicesConfirmation'
        :closeOnEsc="true"
        type='medium'
      >
        <div class="content form-group" slot="body">
          <p>Providing services in the {{purchaseOrder.PO.billTo.address.country}} means that the Supplier was present in the {{purchaseOrder.PO.billTo.address.country}} and physically provided services outlined in their invoice within the US. <i>Your selection will help determine whether withholding tax is deducted from the payment.</i></p>
        </div>
      </ed-modal>

      <ed-modal
        :show='modals.selectSupplierCountry'
        title='Supplier Country'
        :primaryButton='Submit'
        :secondaryButton='Cancel'
        @close='closeSupplierCountrySelection'
        @validate="onSupplierCountrySelected"
        validation="true"
        :closeOnEsc="true"
        type='extra-small'
      >
        <div class="content form-group" slot="body">
          <p>In order to proceed with this payment, please select the country of the supplier.</p>
          <ed-select
              field-label='Supplier Country'
              placeholder='Select Country'
              :options='countries'
              v-model='supplierCountry'
              required
              label='Value'
              track-by='Value'
              @open='fetchCountries'
              @select='selectCountry($event)'
              :error='showCountryMissing'
              :helptip-body='showCountryMissing && "Please select a Country." || ""'
              helptipHeader=''
              name='supplier_country'
            ></ed-select>
        </div>
      </ed-modal>
    </div>
  </div>

</template>

<script>

import { attach } from './override'
import { Eved, EventBus } from '@/event-bus'
import Events from '@/core/events/events'

import { default as legacySteps } from '@/components/LegacyContainer'
import TheToolbar from './components/TheToolbar'

import invoiceInformation from './InvoiceInformation'
import invoiceInformationModel from './models/invoice-information-model'

import budgetSnapshot from './BudgetSnapshot'
import budgetSnapshotModel from './models/budget-snapshot-model'

import budgetScanSnapshot from './BudgetInvoiceScanSnapshot'
import budgetScanSnapshotModel from './models/budget-scan-snapshot-model'

import purchaseOrder from './models/purchaseOrder'
import settingsModel from './models/settings-model'

import lineItemsSection from './LineItemsSection'
import LineItemStore from './dialogs/LineItem/LineItemStore'

import highAllocationSection from './HighAllocationSection.vue'

import comments from './components/comments'

import ccAllocationsModel from './models/cc-allocation-model.js'
import pairsAllocationsModel from './models/bu-and-cc-allocation-model.js'
import categoryAllocationsModel from './models/category-allocation-model.js'
import AllocationsStore from './AllocationsSection/AllocationsStore.js'

import batch from './models/batch-settings.js'
import modals from './models/modals-settings.js'
import batchGridModel from './models/batch-invoice.js'
import Grid from '@/components/Grid'

import Api from './api'
import EventsSection from './EventsSection/EventsSection.js'
import formatter from '@/core/charts/helper/formatter.js'
import constants from './models/line-item-constants'
import ReviewEmailModal from './ReviewEmailModal.vue'

let api = new Api()

export default {
  name: 'InvToPay',
  components: {
    legacySteps,
    TheToolbar,
    invoiceInformation,
    budgetSnapshot,
    lineItemsSection,
    comments,
    highAllocationSection,
    budgetScanSnapshot,
    Grid,
    ReviewEmailModal
  },

  created() {
    EventBus.$off('checkEoNumber')
    EventBus.$on('checkEoNumber', this.handleSupplierStatus)

    this.batch = utils.copy(batch)
    this.onCreated()

    EventBus.$off('zipEmailSent')
    EventBus.$on('zipEmailSent', () => {
      this.$notifications.notify({
        message: `Email has been sent to the supplier!`,
        type: 'info',
        placement: { from: 'top', align: 'center' }
      })
    })
  },

  activated() {
    this.submitDisabled = false
    if (this.createdWasCalled === false) {
      this.initInvoice()
    }
  },

  deactivated() {
    this.onDeactivated()
  },

  beforeLeave() {
    if (!this.purchaseOrderInitialModel && !this.purchaseOrder) return true
    let poValid = this.validatePurchaseOrderBeforeLeave()
    let invoiceValid = this.validateInvoiceBeforeLeave()

    let valid = poValid && invoiceValid

    if (this.batch.invoices.length > 0 && !this.batch.submitAllInvoices) {
      // TODO: maybe use another message !?
      valid = false
    }

    return valid
  },

  data() {
    return {
      showReviewEmailModal: false,
      reviewEmailContent: '',
      cancelClicked: false,
      paymentInvoiceId: 0,
      programId: 0,

      duplicateInvoices: [],
      showDuplicateInvoiceStep1: false,
      showDuplicateInvoiceStep2: false,
      allowDuplicateInvoices: false,

      showOverBudgetWarning: false,
      showOverBudgetWarningBeforeSendingToScan: false,
      showSupplierZipCodeWarning: false,
      overBudgetWarningBeforeSendingToScanWasAccepted: false,
      supplierZipCodeWarningWasAccepted: false,
      showPendingScanningWarning: false,
      showDifferentAmountWarning: false,
      showUnitPriceAtZeroWarning: false,
      showBudgetAlmostUsedNotification: false,
      overBudgetModel: {
        Context: ''
      },
      invoiceInformationInitialModel: {},
      purchaseOrderInitialModel: {},

      invoiceInformationModel,
      purchaseOrder,
      settingsModel,
      budgetSnapshotModel,
      budgetScanSnapshotModel,

      ccAllocations: null,
      pairsAllocations: null,
      categoriesAllocations: null,

      costCentersForScan: null,
      pairsForScan: null,

      activeStep: {},
      tabs: [],
      loading: true,
      dependencies: {
        api: this.api,
        loggedInUserID: 0,
        useSavingsFields: false
      },
      showValidationError: false,
      validationErrorTitle: '',
      validationTitle: '',
      useHighLevelAllocation: null,
      currencyCode: 'USD',
      approvalCheckboxOptions: [
        {
          label: 'I approve the submission of this invoice',
          status: false
        }
      ],
      additionalInvoiceConfirmationOptions: [
        {
          status: false
        }
      ],
      approvalCheckboxError: false,
      approvalNoteError: false,
      additionalInvoiceConfirmationError: false,
      InvoiceAmount: 0,
      VATAmount: 0,
      eventsSectionData: null,
      notInvoicedAmount: null,
      createdWasCalled: false,
      submitDisabled: false,
      decouplingSettings: [],
      initData: {},
      user: {},
      poWasLoaded: false,
      batch,
      batchGridModel,
      modals,
      submitAfterConfirmation: false,
      countries: [],
      supplierCountry: {},
      showCountryMissing: false,
      checkZipMatches: false,
      isEvedSupplier: false
    }
  },

  computed: {
    getBatchGridModel() {
      return batchGridModel.get(this.$user.$.settings)
    },

    divisionLabel() {
      if (this.settingsModel.CompanySettings.EditingInvoiceScan) {
        return this.initData.sender.company.DivisionLabel
      }
    },
    getBatchPopupMessage() {
      let msg = 'All selected invoices will be submitted'
      if (this.settingsModel.CompanySettings.AllowInvoiceScanning) {
        msg += ' and will be scanned in 1 business day.'
      }
      msg += '. Any unchecked invoices will be saved as drafts.'
      return msg
    },

    noOfDraftInvoicesInBatch() {
      return this.batch.invoices.length - (this.batch.selectedInvoices || []).length
    },

    noOfInvoicesToSubmit() {
      return this.batch.invoices.length - this.noOfDraftInvoicesInBatch
    },

    getSubtitle() {
      return this.settingsModel && this.settingsModel.CompanySettings &&
        this.settingsModel.CompanySettings.EditingInvoiceScan
        ? this.invoiceInformationModel.InvoiceNumber
        : ' '
    },

    programLabel() {
      return global.app.$user.sessionSettings.CompanySettings.ProgramLabel.toLowerCase()
    },

    showCommentsBtn() {
      return this.settingsModel &&
        this.settingsModel.CompanySettings &&
        this.settingsModel.CompanySettings.EditingInvoiceScan === false
    },

    eventsSectionObj() {
      if (this.eventsSectionData === null) {
        this.eventsSectionData = new EventsSection(this.dependencies)
      }
      return this.eventsSectionData
    },

    showSaveBtn() {
      return (this.isCreateInvoice() || this.settingsModel.CompanySettings.EditingInvoiceScan === true || this.invoiceInformationModel.IsDraft) &&
              this.settingsModel.CompanySettings.AllowInvoiceScanning === false &&
              !this.isP2PInvoice &&
              this.batch.invoices.length === 0
    },

    showAddAnotherBtn() {
      return this.paymentInvoiceId <= 0
    },

    showSubmitBatchBtn() {
      return this.batch.invoices.length > 0 && this.paymentInvoiceId <= 0
    },

    showLineItems() {
      return this.isP2PInvoiceWithAddEditItems ||
        (!this.isP2PInvoice && this.purchaseOrder.PO.programDetails.uniqueId &&
        this.purchaseOrder.PO.programDetails.uniqueId !== constants.defaultUniqueId)
    },
    isBeforeSendingToScan() {
      return !this.isP2PInvoice && this.settingsModel.CompanySettings.AllowInvoiceScanning === true
    },
    canAddLineItems() {
      return (this.useHighLevelAllocation === false && this.settingsModel.CompanySettings.AllowInvoiceScanning === false) ||
        this.isP2PInvoiceWithAddEditItems
    },
    showCCAllocations() {
      let showCCs = false
      if (!this.isCreateInvoice()) {
        showCCs = this.ccAllocations &&
                  this.ccAllocations.AllocationList.length !== 0 ||
                  this.ccAllocations.WasReset ||
                  (this.ccAllocations.AllocationList.length === 0 && this.ccAllocations.GridModel['*Name'].options.length !== 0)
      } else {
        showCCs = this.settingsModel.CompanySettings.HasCostCenter &&
                  !this.settingsModel.CompanySettings.UseBusinessUnitAndCostCenterMapping &&
                  this.ccAllocations &&
                  this.ccAllocations.AllocationList.length !== 0 || this.ccAllocations.WasReset
      }
      return showCCs
    },
    showPairsAllocations() {
      let showPairs = false
      if (!this.isCreateInvoice()) {
        showPairs = this.pairsAllocations &&
                    this.pairsAllocations.AllocationList.length !== 0 || this.pairsAllocations.WasReset ||
                    (this.pairsAllocations.AllocationList.length === 0 && this.pairsAllocations.GridModel['*Name'].options.length !== 0)
      } else {
        showPairs = this.settingsModel.CompanySettings.UseBusinessUnitAndCostCenterMapping &&
                    this.pairsAllocations &&
                    this.pairsAllocations.AllocationList.length !== 0 || this.pairsAllocations.WasReset
      }
      return showPairs
    },
    showCategoryAllocations() {
      return this.settingsModel.CompanySettings.AllocateCategories
    },
    isP2PInvoice() {
      return this.invoiceInformationModel.InvoiceWorkflow && this.invoiceInformationModel.InvoiceWorkflow.indexOf('P2PInvoice') !== -1
    },
    isP2PInvoiceWithAddEditItems() { // allowed actions: edit items, add/edit taxes
      return this.isP2PInvoice && this.settingsModel.CompanySettings.AllowEditItem
    },
    showAdditionalInvoiceConfirmation() {
      return this.settingsModel.CompanySettings.RequireAdditionalInvoiceConfirmation && this.settingsModel.CompanySettings.EditingInvoiceScan === false
    },
    maxInvoiceAmount() {
      return this.isCreateInvoice()
        ? this.notInvoicedAmount
        : this.invoiceInformationInitialModel.InvoiceAmount + this.notInvoicedAmount
    },
    categRefIdRequired() {
      return this.settingsModel.CompanySettings.IsRequiredCategRefID
    },

    getDecouplingSettings() {
      return this.decouplingSettings
    }
  },

  watch: {
    'invoiceInformationModel.SupplierZipCode': 'checkIfZipMatches',
    'invoiceInformationModel.Supplier.SupplierZIP': 'checkIfZipMatches'
  },

  methods: {
    onCreated() {
      this.createdWasCalled = true

      EventBus.$off(Eved.I2P.CHANGE_ALLOCATION)
      EventBus.$on(Eved.I2P.CHANGE_ALLOCATION, e => {
        if (e.data && e.data.Type && e.data.Allocation) {
          this.updateAllocationModel(e.data.Type, e.data.Allocation, e.data.ValidAllocations)
        }

        this.updatePOItemsAfterAllocationChange()
      })

      EventBus.$off(Eved.I2P.ADD_CC_FOR_SCAN)
      EventBus.$on(Eved.I2P.ADD_CC_FOR_SCAN, this.updateAllocationForScan)

      EventBus.$off(Eved.I2P.PROGRAM_SELECTED)
      EventBus.$on(Eved.I2P.PROGRAM_SELECTED, e => {
        // update program
        var programDetails = JSON.parse(e.data.ProgramDetails)
        if (programDetails.CurrencyCode) {
          this.currencyCode = programDetails.CurrencyCode
        }

        var programUniqueId = programDetails.UniqueId
        this.programId = programDetails.ProgramID

        if (this.purchaseOrder && this.purchaseOrder.PO) {
          if (
            this.purchaseOrder.PO.programDetails) {
            this.purchaseOrder.PO.programDetails.uniqueId = programUniqueId
            if (programDetails.CurrencyCode) {
              this.purchaseOrder.PO.programDetails.currencyCode = programDetails.CurrencyCode
            }
            // i2p and bu/cc allocation
            this.purchaseOrder.PO.events[0].uniqueId = e.data.EventKey
          }

          this.purchaseOrder.PO.allocateProcessingFeeToEveryCostCenter = e.data.AllocateProcessingFeeToEveryCostCenter
        }

        // update budgetSnapshot
        var budgetSnapshotInfo = JSON.parse(e.data.BudgetSnapshotInfo)
        budgetSnapshotInfo.initDataForEdit = true
        budgetSnapshotInfo.ProgramIsSelected = true
        budgetSnapshotInfo.useHighLevelAllocation = this.useHighLevelAllocation
        if (
          this.invoiceInformationModel &&
          this.invoiceInformationModel.CurrencyCode) {
          budgetSnapshotInfo.InvoiceCurrencyCode = this.invoiceInformationModel.CurrencyCode
        }

        if (e.data.InvoiceWorkflowStr) {
          this.invoiceInformationModel.InvoiceWorkflow = e.data.InvoiceWorkflowStr
          budgetSnapshotInfo.InvoiceWorkflow = e.data.InvoiceWorkflowStr

          if (this.isP2PInvoice) {
            this.useHighLevelAllocation = false
            budgetSnapshotInfo.useHighLevelAllocation = false
            this.invoiceInformationModel.UseHighLevelAllocation = false
          }
        }

        this.budgetSnapshotModel = budgetSnapshotInfo

        // when the program is changed all the items will be deleted
        if (this.purchaseOrder &&
          this.purchaseOrder.PO &&
          this.purchaseOrder.PO.events &&
          this.purchaseOrder.PO.events[0]) {
          this.purchaseOrder.PO.events[0].lineItems = []
          if (!this.invoiceInformationModel.isEditInit) {
            this.recalculatePO()
          }
        }

        if (e.data.BillTo && e.data.BillTo.company) {
          this.setBillToData(e.data.BillTo.company)
        }

        if (e.data.POTerms) {
          this.setPOTerms(e.data.POTerms)
        }

        this.invoiceInformationModel.UseSavingsFields = e.data.UseSavingsFields
        this.additionalInvoiceConfirmationOptions = [
          {
            status: false,
            label: e.data.AdditionalInvoiceConfirmationText
          }
        ]
        if (e.data.ApprovalNotePlaceholder) {
          this.invoiceInformationModel.ApproveNote = e.data.ApprovalNotePlaceholder
          this.settingsModel.CompanySettings.ApprovalNotePlaceholder = e.data.ApprovalNotePlaceholder
        }
        if (this.useHighLevelAllocation) {
          this.setHighLevelComboAndGridItems(e.data.InvoiceCostCenters, e.data.InvoiceCategories, e.data.InvoiceBuAndCcPairs,
            e.data.ProgramCostCenters, e.data.ProgramCategories, e.data.ProgramBuAndCcPairs)
        }
      })

      EventBus.$off(Eved.I2P.VALIDATION_COMPLETED)
      EventBus.$on(Eved.I2P.VALIDATION_COMPLETED, e => {
        if (e.data.validInvoice &&
          (this.cancelClicked || e.data.gotoNextTab)) {
          this.cancelInvoice()
        }
      })

      EventBus.$off(Eved.I2P.DUE_DATE_COMPUTED_ON_INIT)
      EventBus.$on(Eved.I2P.DUE_DATE_COMPUTED_ON_INIT, e => {
        if (e.data.dueDate) {
          this.invoiceInformationInitialModel.DueDate = e.data.dueDate
        }
      })

      EventBus.$off(Eved.I2P.INVOICE_AMOUNT_CHANGED)
      EventBus.$on(Eved.I2P.INVOICE_AMOUNT_CHANGED, e => {
        if (e.data && !isNaN(e.data.amount)) {
          this.InvoiceAmount = Number(e.data.amount)
          this.supplierZipCodeWarningWasAccepted = false
        }

        if (this.isP2PInvoice) {
          this.invoiceInformationModel.InvoiceAmount = Number(e.data.amount)
          this.notInvoicedAmount = e.data.notInvoicedAmount
          this.invoiceInformationModel.NotInvoicedAmount = e.data.notInvoicedAmount
          this.updateSnaphot()
        }
      })

      EventBus.$off(Eved.I2P.INVOICE_INITIAL_TOTAL_AMOUNT_CHANGED)
      EventBus.$on(Eved.I2P.INVOICE_INITIAL_TOTAL_AMOUNT_CHANGED, e => {
        if (e.data.amount) {
          this.invoiceInformationModel.InitialTotalAmount = Number(e.data.amount)
          this.overBudgetWarningBeforeSendingToScanWasAccepted = false
          this.supplierZipCodeWarningWasAccepted = false
        }
      })

      EventBus.$off(Eved.I2P.VAT_AMOUNT_CHANGED)
      EventBus.$on(Eved.I2P.VAT_AMOUNT_CHANGED, e => {
        if (e.data && !isNaN(e.data.vatgstAmount)) {
          this.VATAmount = Number(e.data.vatgstAmount)
          this.overBudgetWarningBeforeSendingToScanWasAccepted = false
          this.supplierZipCodeWarningWasAccepted = false
        }
      })

      EventBus.$off(Eved.I2P.REMOVE_P2P_PO)
      EventBus.$on(Eved.I2P.REMOVE_P2P_PO, e => {
        if (this.purchaseOrder.PO) {
          this.purchaseOrder.PO.uniqueId = ''
        }
      })

      EventBus.$off(Eved.I2P.SUPPLIER_COUNTRY_CONFIRMED)
      EventBus.$on(Eved.I2P.SUPPLIER_COUNTRY_CONFIRMED, this.onSupplierCountryConfirmed)

      this.initInvoice()
    },

    onDeactivated() {
      this.submitDisabled = false
      this.cancelClicked = false
      this.useHighLevelAllocation = null
      this.createdWasCalled = false
      this.batch.addAnotherInvoice = false
      this.batch.submitAllInvoices = false
      this.senderOnInit = null
      this.itemsOnInit = null
      this.initData = {}
      this.poWasLoaded = false
      this.countries = []
      this.supplierCountry = {}
      this.showCountryMissing = false
      this.clearTabs()
      this.resetJSONmodel()
      this.resetDuplicateInvoice()
    },

    async initInvoice() {
      this.$nextTick(() => {
        this.resetJSONmodel()

        this.useHighLevelAllocation = null
        this.programId = 0

        this.highlightTabs()
        this.loading = true

        // should not be included in resetJSONmodel()
        if (this.$route.query.from !== 'batch') {
          this.batch.invoices = []
          this.batch.selectedInvoices = null
        }

        this.initUploadInvoice()
          .then(r => {
            this.initData = r.data

            this.useHighLevelAllocation = this.initData.useHighLevelAllocation
            this.paymentInvoiceId = this.initData.paymentInvoiceId

            // settings
            if (this.initData.settings) {
              this.settingsModel.CompanySettings = this.initData.settings
              this.additionalInvoiceConfirmationOptions[0].label = this.settingsModel.CompanySettings.AdditionalInvoiceConfirmationText
            }

            // current user data
            if (this.initData.user && this.initData.user !== '') {
              this.user = this.initData.user

              this.dependencies.loggedInUserID = this.user.buyerUserID
              this.dependencies.canEditCategRefId = this.user.canEditCategRefId
            }

            // workflow
            if (this.initData.invoice) {
              this.invoiceInformationModel.InvoiceWorkflow = this.initData.invoice.InvoiceWorkflow
            }

            this.loadInitialData()
          })
      })
    },

    loadPurchaseOrder(PO, items) {
      if (this.isP2PInvoiceWithAddEditItems) {
        // p2p decoupling
        if (this.paymentInvoiceId > 0) { // edit
          PO.events = this.updateQtyAndUnitsFromInvoiceOnEdit(PO.events, items)
        } else { // create
          PO.events = this.updateQtyAndUnitsFromPOonCreate(PO.events, items)
        }
        PO.events = this.store().getEventsWithOrderedLineItems(PO.events)
      } else if (this.paymentInvoiceId > 0 && Array.isArray(items)) {
        // i2p: update InvoiceItemKey on PO items
        PO.events = this.store().updateItemsForI2PInvoiceOnEdit(PO.events, items)
      }

      this.purchaseOrder.PO = PO
      this.poWasLoaded = true
    },

    loadInitialData() {
      let data = this.initData

      if (data.invoice) {
        for (var key in data.invoice) {
          if (data.invoice.hasOwnProperty(key)) {
            this.invoiceInformationModel[key] = data.invoice[key]
          }
        }
      }

      if (typeof data.program === 'object') {
        this.programId = data.program.ProgramID
        this.invoiceInformationModel.ProgramID = data.program.ProgramID
        this.invoiceInformationModel.ProgramName = data.program.ProgramName
        this.invoiceInformationModel.DivisionID = data.program.DivisionID
        this.invoiceInformationModel.BusinessUnitID = data.program.BusinessUnitID
        this.invoiceInformationModel.DivisionName = data.program.DivisionName
        this.invoiceInformationModel.ProgramCurrencyCode = data.program.ProgramCurrencyCode
      }

      if (this.$route.query.fusionPoId) {
        this.invoiceInformationModel.POid = this.$route.query.fusionPoId
      }

      if (data.poTerms) {
        this.setPOTerms(data.poTerms)
      }

      if (typeof data.supplier === 'object') {
        this.invoiceInformationModel.Supplier.VendorID = data.supplier.SupplierID
        this.invoiceInformationModel.Supplier.VendorUserID = data.supplier.SupplierUserID
        this.invoiceInformationModel.Supplier.SupplierVCI = data.supplier.SupplierVCI
        this.invoiceInformationModel.Supplier.CountryName = data.supplier.SupplierCountryName
        this.invoiceInformationModel.SupplierId = data.supplier.SupplierID
        this.invoiceInformationModel.SupplierUserId = data.supplier.SupplierUserID
        this.invoiceInformationModel.MyVendorTempEONumber = data.supplier.MyVendorTempEONumber
        this.invoiceInformationModel.SupplierName = data.supplier.SupplierName
        this.invoiceInformationModel.isEditInit = true

        if (data.supplier.SupplierName) {
          setTimeout(() => {
            EventBus.$emit(Eved.I2P.ADDED_NEW_SUPPLIER, {
              SupplierInfoVM: {
                SupplierName: data.supplier.SupplierName,
                SupplierContactName: data.supplier.SupplierContactName,
                VatGstId: data.supplier.VatGstId,
                EmailAddress: data.supplier.SupplierEmail,
                SupplierPhone: data.supplier.SupplierPhone,
                VendorID: data.supplier.SupplierID,
                VendorUserID: data.supplier.SupplierUserID,
                DiscountPaymentTermID: data.invoice && data.invoice.DiscountPaymentTermID,
                DiscountName: data.invoice && data.invoice.DiscountName,
                DiscountDisplayName: data.invoice && data.invoice.DiscountDisplayName,
                DiscountDaysNo: data.invoice && data.invoice.DiscountDaysNo,

                SupplierAddress1: data.supplier.SupplierAddress1,
                SupplierAddress2: data.supplier.SupplierAddress2,
                SupplierCity: data.supplier.SupplierCity,
                SupplierState: data.supplier.SupplierState,
                SupplierRegion: data.supplier.SupplierRegion,
                SupplierZIP: data.supplier.SupplierZIP,
                CountryName: data.supplier.SupplierCountryName,
                VendorType: data.supplier.VendorType,
                HasDefaultImmediatePayment: data.supplier.HasDefaultImmediatePayment,
                WithholdingTaxPercent: data.supplier.WithholdingTaxPercent,
                Currencies: data.supplier.Currencies
              },
              CardInfo: {
                logoPath: ' ',
                Logo: data.supplier.SupplierLogo
              },
              VendorId: data.supplier.SupplierID,
              VendorVCI: data.supplier.SupplierVCI > 0 ? data.supplier.SupplierVCI : data.supplier.MyVendorTempEONumber,
              VendorUserID: data.supplier.SupplierUserID,
              IsInitSupplier: true,
              InvoiceCurrencyCode: data.invoice.CurrencyCode
            })
          }, 200)
        }
      }

      if (data.invoice) {
        this.invoiceInformationModel.PaymentInvoiceId = this.paymentInvoiceId
        this.invoiceInformationModel.InvoiceDate = data.invoice.InvoiceDate === ''
          ? null
          : data.invoice.InvoiceDate
        this.invoiceInformationModel.DueDate = data.invoice.DueDate === ''
          ? null
          : data.invoice.DueDate
        this.invoiceInformationModel.InvoiceAmount = data.invoice.InvoiceAmount || this.invoiceInformationModel.InvoiceAmount
        this.invoiceInformationModel.VATGSTAmount = data.invoice.InvoiceVATGSTAmount || this.invoiceInformationModel.VATGSTAmount
        this.invoiceInformationModel.POid = data.invoice.POId || this.$route.query.fusionPoId

        this.currencyCode = data.invoice.CurrencyCode
        this.dependencies.useSavingsFields = data.invoice.UseSavingsFields
        this.notInvoicedAmount = data.invoice.NotInvoicedAmount

        this.invoiceInformationModel.IsFromEmailForScanning = data.invoice.IsFromEmailForScanning || false

        this.InvoiceAmount = data.invoice.InvoiceAmount || this.invoiceInformationModel.InvoiceAmount
        this.VATAmount = data.invoice.InvoiceVATGSTAmount || this.invoiceInformationModel.VATGSTAmount
        if (data.invoice.Attachments && data.invoice.Attachments.length > 0) {
          this.invoiceInformationModel.Attachments = data.invoice.Attachments
        } else {
          this.invoiceInformationModel.Attachments = []
        }

        if (this.settingsModel && this.settingsModel.CompanySettings) {
          this.invoiceInformationModel.ApproveNote = this.settingsModel.CompanySettings.ApprovalNotePlaceholder || ''
        }

        // used timeout to make sure invoiceInformation.created is initiated and mapExistingItems.can be called
        setTimeout(function() {
          EventBus.$emit(Eved.I2P.MAP_INVOICE_INFORMATION_FIELDS)
        }, 50)
      }

      // IMPORTANT: remove after removing from backend the save/submit/get of the PO
      if (data.purchaseOrder && data.purchaseOrder !== '') {
        this.loadPurchaseOrder(JSON.parse(data.purchaseOrder), data.invoice.Items)
      } else if (/* !this.poWasLoaded && */typeof data.program === 'object') {
        this.purchaseOrder.PO.programDetails.uniqueId = data.program.UniqueId
        this.purchaseOrder.PO.programDetails.internalProgramId = data.program.InternalProgramId
        this.purchaseOrder.PO.programDetails.name = data.program.ProgramName
        this.purchaseOrder.PO.programDetails.isNewProgram = false
        this.purchaseOrder.PO.programDetails.currencyCode = data.program.ProgramCurrencyCode

        if (data.program.EventUniqueId &&
          this.purchaseOrder.PO.events && this.purchaseOrder.PO.events[0]) {
          // i2p and bu/cc allocation
          this.purchaseOrder.PO.events[0].uniqueId = data.program.EventUniqueId
        }
      }

      if (this.purchaseOrder && this.purchaseOrder.PO && this.isCreateInvoice()) {
        this.purchaseOrder.PO.allocateProcessingFeeToEveryCostCenter = this.settingsModel.CompanySettings.AllocateProcessingFeeToEveryCostCenter
      }

      if (data.sender) {
        this.invoiceInformationModel.CreatedByInfo = {
          Name: data.sender.user.FirstName + ' ' + data.sender.user.LastName,
          Email: data.sender.user.Email,
          Phone: data.sender.user.PhoneNumber
        }

        this.setBillToData(data.sender.company)

        this.purchaseOrder.PO.preparedBy = {
          email: data.sender.user.Email,
          firstName: data.sender.user.FirstName,
          lastName: data.sender.user.LastName,
          office: data.sender.user.PhoneNumber,
          mobile: data.sender.user.Mobile
        }
        this.purchaseOrder.PO.companyInfo = {
          vendorId: data.sender.company.VendorID,
          name: data.sender.company.Name,
          logo: data.sender.company.Logo,
          address: {
            address1: data.sender.company.Address,
            address2: data.sender.company.Address2,
            city: data.sender.company.City,
            state: data.sender.company.State,
            region: data.sender.company.Region,
            zipCode: data.sender.company.ZipCode,
            country: data.sender.company.Country
          }
        }
      }

      if (data.budgetSnapshotInfo) {
        var snapshot = data.budgetSnapshotInfo
        snapshot.initDataForEdit = true
        snapshot.useHighLevelAllocation = this.useHighLevelAllocation
        snapshot.InvoiceWorkflow = data.invoice.InvoiceWorkflow
        if (typeof data.program === 'object') {
          snapshot.ProgramIsSelected = true
        }
        this.budgetSnapshotModel = snapshot
      }
      if (data.budgetInvoiceScanSnapshot) {
        this.budgetScanSnapshotModel = data.budgetInvoiceScanSnapshot
      }

      // TODO: update program and supplier
      if (this.useHighLevelAllocation) {
        this.initHighLevelAllocation()

        if (data.invoiceCostCenters || data.invoiceCategories || data.invoiceBuAndCcPairs) {
          this.setHighLevelComboAndGridItems(data.invoiceCostCenters, data.invoiceCategories, data.invoiceBuAndCcPairs,
            data.programCostCenters, data.programCategories, data.programBuAndCcPairs)
        }

        this.recalculatePO()
      }

      if (data.scanAllocationOption || data.hasMultipleAllocation) {
        this.invoiceInformationModel.allocationType = data.allocationType
        this.invoiceInformationModel.scanAllocationOption = data.scanAllocationOption
        this.invoiceInformationModel.hasMultipleAllocation = data.hasMultipleAllocation
      }

      this.invoiceInformationModel.PaymentTypeID = this.invoiceInformationModel.PaymentTypeID || data.defaultPaymentType.Id
      this.invoiceInformationModel.PaymentType = this.invoiceInformationModel.PaymentType || data.defaultPaymentType.Value
      this.invoiceInformationModel.DiscountPaymentTermID = data.defaultDiscount.Key
      this.invoiceInformationModel.DiscountDisplayName = data.defaultDiscount.Value
      this.invoiceInformationModel.DiscountName = data.defaultDiscount.Name
      EventBus.$emit(Eved.I2P.MAP_DEFAULT_PAYMENT_TYPE_DISCOUNT)

      this.cacheInitialData()
    },

    getDecouplingCompanySettings() {
      return {
        AllowExceedPOQty: this.isP2PInvoice && this.settingsModel.CompanySettings.AllowEditItem &&
          this.settingsModel.CompanySettings.AllowInvoiceToExceedPOQty,
        ExcludeTaxes: this.isP2PInvoice && this.settingsModel.CompanySettings.AllowEditItem &&
          this.settingsModel.CompanySettings.ExcludeTaxesFromPOThreshold
      }
    },

    updateQtyAndUnitsFromPOonCreate(poEvents, invoicedItems) {
      let settings = this.getDecouplingCompanySettings()

      this.decouplingSettings = this.store().getDecouplingSettingsPerItem(poEvents,
        invoicedItems, settings)

      return this.store().updateEventsOnCreate(poEvents, invoicedItems, this.decouplingSettings)
    },

    updateQtyAndUnitsFromInvoiceOnEdit(poEvents, invoiceItems) {
      let settings = this.getDecouplingCompanySettings()

      this.decouplingSettings = this.store().getDecouplingSettingsPerItem(poEvents,
        invoiceItems, settings)

      return this.store().getEventsWithChangesFromInvoice(poEvents, invoiceItems, this.decouplingSettings)
    },

    store() {
      return new LineItemStore(this.api)
    },
    allocationsStore() {
      return new AllocationsStore(this.api)
    },
    back() {
      if (this.mustSave) {
        this.save()
      }
    },

    isCreateInvoice() {
      return parseInt(this.$route.query.invoiceId) <= 0 ||
        isNaN(parseInt(this.$route.query.invoiceId))
    },

    initHighLevelAllocation() {
      this.ccAllocations = {
        Type: 'CostCenters',
        AllocationList: [],
        SelectionList: [],
        GridTitle: 'Cost Center',
        GridModel: ccAllocationsModel.get([], []),
        InvoiceInformation: {
          programId: this.invoiceInformationModel.ProgramID
        },
        Label: 'Cost Center',
        WasReset: false,
        TotalPercent: 0,
        ValidAllocations: true
      }
      this.pairsAllocations = {
        Type: 'Pairs',
        AllocationList: [],
        SelectionList: [],
        GridTitle: 'Business Unit & Cost Center',
        GridModel: pairsAllocationsModel.get([], []),
        InvoiceInformation: {
          programId: this.invoiceInformationModel.ProgramID
        },
        Label: 'Pair',
        WasReset: false,
        TotalPercent: 0,
        ValidAllocations: true
      }
      this.categoriesAllocations = {
        Type: 'Categories',
        AllocationList: [],
        SelectionList: [],
        GridTitle: 'Category',
        GridModel: categoryAllocationsModel.get([], [], this.user.canEditCategRefId),
        InvoiceInformation: {
          programId: this.invoiceInformationModel.ProgramID
        },
        Label: 'Category',
        WasReset: this.settingsModel.CompanySettings.AllocateCategories,
        TotalPercent: 0,
        ValidAllocations: true
      }
    },

    setHighLevelComboAndGridItems(invCC, invCategories, invPairs, pgCC, pgCategories, pgPairs) {
      this.ccAllocations.AllocationList = invCC
      if (invCC.length === 1) {
        this.ccAllocations.AllocationList.map(item => {
          item.Percent = 100
          return item
        })
      }
      this.ccAllocations.GridModel['*Name'].options = pgCC
      this.ccAllocations.SelectionList = pgCC
      this.ccAllocations.GridModel['*Name'].selectedOptions = invCC
      this.ccAllocations.InvoiceInformation.programId = this.invoiceInformationModel.ProgramID
      this.ccAllocations.WasReset = invCC.length > 0

      this.pairsAllocations.AllocationList = invPairs
      if (invPairs.length === 1) {
        this.pairsAllocations.AllocationList.map(item => {
          item.Percent = 100
          return item
        })
      }
      this.pairsAllocations.GridModel['*Name'].options = pgPairs
      this.pairsAllocations.SelectionList = pgPairs
      this.pairsAllocations.GridModel['*Name'].selectedOptions = invPairs
      this.pairsAllocations.InvoiceInformation.programId = this.invoiceInformationModel.ProgramID
      this.pairsAllocations.WasReset = invPairs.length > 0

      this.categoriesAllocations.AllocationList = invCategories
      this.categoriesAllocations.GridModel['*Name'].options = pgCategories
      this.categoriesAllocations.SelectionList = pgCategories
      this.categoriesAllocations.GridModel['*Name'].selectedOptions = invCategories
      this.categoriesAllocations.InvoiceInformation.programId = this.invoiceInformationModel.ProgramID
    },

    validateAllocationsTotal() {
      if (this.ccAllocations.WasReset && this.ccAllocations.TotalPercent !== 100 ||
      this.pairsAllocations.WasReset && this.pairsAllocations.TotalPercent !== 100 ||
      this.categoriesAllocations.WasReset && this.categoriesAllocations.TotalPercent !== 100) {
        return false
      }
      return true
    },
    validateAllocations() {
      let isValid = true
      if (!this.ccAllocations.ValidAllocations ||
      !this.pairsAllocations.ValidAllocations ||
      !this.categoriesAllocations.ValidAllocations) {
        isValid = false
      }
      return isValid
    },
    validatePurchaseOrderBeforeLeave() {
      let oldPO = this.purchaseOrderInitialModel
      let newPO = this.purchaseOrder.PO

      // line items
      let oldItemKeys = []
      oldPO && oldPO.events && oldPO.events.length > 0 &&
        oldPO.events.map((ev, evindex) => {
          ev.lineItems.map((item, index) => {
            if (item.itemKey) {
              oldItemKeys.push(item.itemKey)
            }
          })
        })

      let lineItemsValid = newPO && newPO.events && newPO.events.length > 0
      if (lineItemsValid) {
        newPO.events.map((newEvent, evindex) => {
          let oldEvent = oldPO.events && oldPO.events.filter(e => e.uniqueId === newEvent.uniqueId)[0]
          if (!oldEvent ||
            newEvent.lineItems.filter(item => oldItemKeys.indexOf(item.itemKey) === -1 && item.itemKey).length !== 0 ||
            newEvent.lineItems.length !== oldEvent.lineItems.length) {
            lineItemsValid = false
          } else {
            if (newEvent.lineItems.length === oldEvent.lineItems.length) {
              let skipNameValidation = this.invoiceInformationModel.UseHighLevelAllocation

              newEvent.lineItems.forEach(function (nItem, index) {
                var oItem = oldEvent.lineItems[index]

                if (parseInt(nItem.listOrder) !== parseInt(oItem.listOrder) ||
                  parseFloat(nItem.total || 0) !== parseFloat(oItem.total || 0) ||
                  nItem.isFeeDiscountItem !== oItem.isFeeDiscountItem ||
                  ((nItem.name || '') !== (oItem.name || '') && !skipNameValidation) ||
                  (nItem.description || '') !== (oItem.description || '') ||
                  (nItem.internalNote || '') !== (oItem.internalNote || '') ||
                  parseFloat(nItem.quantity || 0) !== parseFloat(oItem.quantity || 0) ||
                  (!nItem.isFeeDiscountItem && (
                    parseFloat(nItem.unitPrice || 0) !== parseFloat(oItem.unitPrice || 0) ||
                    parseFloat(nItem.units || 0) !== parseFloat(oItem.units || 0) ||
                    (nItem.unitType || '') !== (oItem.unitType || '') ||
                    (nItem.referenceId || '') !== (oItem.referenceId || '') ||
                    (nItem.category || '') !== (oItem.category || '') ||
                    parseFloat(nItem.discount || 0) !== parseFloat(oItem.discount || 0) ||
                    parseFloat(nItem.discountAmount || 0) !== parseFloat(oItem.discountAmount || 0) ||
                    parseFloat(nItem.discountPercent || 0) !== parseFloat(oItem.discountPercent || 0)
                  )) ||
                  (nItem.isFeeDiscountItem && (
                    parseInt(nItem.amountTypeId || 0) !== parseInt(oItem.amountTypeId || 0) ||
                    parseFloat(nItem.amountTypeValue || 0) !== parseFloat(oItem.amountTypeValue || 0)
                  )) ||
                  parseFloat(nItem.totalTaxes || 0) !== parseFloat(oItem.totalTaxes || 0) ||
                  parseFloat(nItem.totalFees || 0) !== parseFloat(oItem.totalFees || 0) ||
                  parseFloat(nItem.totalTaxesForFees || 0) !== parseFloat(oItem.totalTaxesForFees || 0) ||
                  parseFloat(nItem.subtotal || 0) !== parseFloat(oItem.subtotal || 0) ||
                  (!nItem.costCenterAndBusinessUnit && oItem.costCenterAndBusinessUnit) ||
                  (nItem.costCenterAndBusinessUnit && !oItem.costCenterAndBusinessUnit) ||
                    (nItem.costCenterAndBusinessUnit.costCenterNo || '') !== (oItem.costCenterAndBusinessUnit.costCenterNo || '') ||
                    (nItem.costCenterAndBusinessUnit.costCenterName || '') !== (oItem.costCenterAndBusinessUnit.costCenterName || '') ||
                    (nItem.costCenterAndBusinessUnit.businessUnitName || '') !== (oItem.costCenterAndBusinessUnit.businessUnitName || '') ||
                    (nItem.costCenterAndBusinessUnit.costCenterAndBusinessUnitName || '') !== (oItem.costCenterAndBusinessUnit.costCenterAndBusinessUnitName || '') ||
                    (nItem.costCenterAndBusinessUnit.costCenterAndBusinessUnitKey || '') !== (oItem.costCenterAndBusinessUnit.costCenterAndBusinessUnitKey || '')
                  ) {
                  lineItemsValid = false
                }
              })
            }
          }
        })
      }

      let poValid = oldPO && newPO &&
        oldPO.uniqueId === newPO.uniqueId &&
        oldPO.programDetails.uniqueId === newPO.programDetails.uniqueId &&
        oldPO.supplier && newPO.supplier && oldPO.supplier.email === newPO.supplier.email &&
        lineItemsValid

      return poValid
    },

    validateInvoiceBeforeLeave() {
      let oldInv = this.invoiceInformationInitialModel
      let newInv = this.invoiceInformationModel

      // invoice attachments
      let oldAttachments = []
      oldInv && oldInv.Attachments &&
        oldInv.Attachments.map((f, i) => {
          oldAttachments.push(f.AttachmentFile)
        })

      let attachmentsValid = newInv && newInv.Attachments &&
        newInv.Attachments.filter(f => oldAttachments.indexOf(f.AttachmentFile) === -1).length === 0
      let invoiceValid = oldInv && newInv &&
        oldInv.PaymentTypeID === newInv.PaymentTypeID &&
        new Date(oldInv.InvoiceDate).toDateString() === new Date(newInv.InvoiceDate).toDateString() &&
        new Date(oldInv.DueDate).toDateString() === new Date(newInv.DueDate).toDateString() &&
        attachmentsValid &&
        oldInv.CurrencyCode === newInv.CurrencyCode &&
        (!this.invoiceInformationModel.UseHighLevelAllocation ||
          ((+oldInv.InvoiceAmount || 0) === (+newInv.InvoiceAmount || 0) && (+oldInv.VATGSTAmount || 0) === (+newInv.VATGSTAmount || 0))
        )

      return invoiceValid
    },

    setBillToData(company) {
      this.purchaseOrder.PO.billTo = {
        companyName: company.Name,
        address: {
          address1: company.Address1,
          address2: company.Address2,
          city: company.City,
          state: company.State,
          region: company.Region,
          zipCode: company.ZipCode,
          country: company.Country,
          vatNo: company.VatNo
        }
      }
    },

    setPOTerms(terms) {
      this.purchaseOrder.PO.terms = terms
    },

    cacheInitialData() {
      this.purchaseOrderInitialModel = utils.copy(this.purchaseOrder.PO)
      this.invoiceInformationInitialModel = utils.copy(this.invoiceInformationModel)
    },

    getInvoiceAmountToValidateAgainstUninvoiced() {
      let finalInvoiceAmount = this.invoiceInformationModel && Number(this.invoiceInformationModel.InvoiceAmount)
      if (!this.isCreateInvoice()) {
        if (finalInvoiceAmount > 0) {
          // positive amount
          if (finalInvoiceAmount > this.invoiceInformationInitialModel.InvoiceAmount || 0) {
            finalInvoiceAmount -= this.invoiceInformationInitialModel.InvoiceAmount || 0
          } else {
            finalInvoiceAmount = 0
          }
        } else if (finalInvoiceAmount < 0) {
          // negative amount
          if (finalInvoiceAmount < this.invoiceInformationInitialModel.InvoiceAmount) {
            finalInvoiceAmount -= this.invoiceInformationInitialModel.InvoiceAmount || 0
          } else {
            finalInvoiceAmount = 0
          }
        }
      }
      return finalInvoiceAmount
    },

    hasAnyPDFAttachment() {
      // exclude readonly files
      if (!this.invoiceInformationModel.Attachments ||
          JSON.stringify(this.invoiceInformationModel.Attachments) === JSON.stringify({}) ||
          this.invoiceInformationModel.Attachments.length <= 0) return false

      let any = false
      this.invoiceInformationModel.Attachments.map((f) => {
        if (!f.ReadOnly && f.AttachmentFile.toLowerCase().endsWith('.pdf')) {
          any = true
        }
      })
      return any
    },

    async isOverBudgetBeforeSendingToScan() {
      if (this.invoiceInformationModel.ProgramID === 0 || this.invoiceInformationModel.InitialTotalAmount <= 0) {
        return false
      }

      let param = {
        programId: this.invoiceInformationModel.ProgramID,
        invoiceAmount: this.invoiceInformationModel.InitialTotalAmount,
        invoiceVatGst: +this.invoiceInformationModel.VATGSTAmount,
        currencyCode: this.invoiceInformationModel.CurrencyCode
      }

      const r = await this.api.post(this.api.$.invoice.i2p.checkOverBudgetBeforeSendingToScan, param)
      if (r.data && r.data.IsOverBudget) {
        this.overBudgetAmountBeforeSendingToScan = r.data.OverBudgetAmount
        return true
      }
      return false
    },

    async checkSupplierZipCode() {
      if (!this.invoiceInformationModel.SupplierZipCode || !this.invoiceInformationModel.Supplier.VendorID || this.invoiceInformationModel.Supplier.VendorID <= 0) {
        return false
      }

      let param = {
        supplierId: this.invoiceInformationModel.Supplier.VendorID,
        zipCode: this.invoiceInformationModel.SupplierZipCode
      }

      const r = await this.api.post(this.api.$.invoice.checkSupplierZipCode, param)
      if (r.data && r.data.ZipCodeIsDifferent) {
        return true
      }
      return false
    },

    checkIfZipMatches() {
      if (this.invoiceInformationModel.SupplierZipCode && this.invoiceInformationModel.Supplier.SupplierZIP) {
        this.checkZipMatches = this.invoiceInformationModel.SupplierZipCode.toLowerCase().trim() === this.invoiceInformationModel.Supplier.SupplierZIP.toLowerCase().trim()
      } else if (typeof this.invoiceInformationModel.SupplierZipCode !== 'undefined' &&
        typeof this.invoiceInformationModel.Supplier.SupplierZIP !== 'undefined') {
        this.checkZipMatches = false
      }
      return this.checkZipMatches
    },

    reviewEmail() {
      try {
        const invoiceId = this.invoiceInformationModel.PaymentInvoiceId

        this.api.get(this.api.$.invoice.reviewEmail, {
          invoiceId: invoiceId
        })
      } catch (ex) {
        console.error('Error sending email:', ex)
      }
    },

    closeReviewEmailModal() {
      this.showReviewEmailModal = false
    },

    openReviewEmailModal() {
      try {
        const paymentInvoiceId = this.invoiceInformationModel.PaymentInvoiceId
        if (!paymentInvoiceId) {
          console.warn('Invoice ID not available')
          return
        }
        this.api.get(`${this.api.$.invoice.reviewEmail}/${paymentInvoiceId}`)
          .then(response => {
            this.reviewEmailContent = response.data
            this.showReviewEmailModal = true
          })
          .catch(ex => console.error('Error getting review email:', ex))
      } catch (ex) {
        console.error('Error getting review email:', ex)
      }
    },
    sendZipMismatchEmail() {
      try {
        const invoiceId = this.invoiceInformationModel.PaymentInvoiceId

        this.api.post(this.api.$.invoice.sendZipMismatchEmail, {
          invoiceId: invoiceId
        })
          .then(response => {
            if (response.status === 200) {
              EventBus.$emit('zipEmailSent')
            } else {
              alert('Failed to send email.')
            }
          })
          .catch(error => {
            console.error('Error sending email: ', error)
          })
      } catch (ex) {
        console.error('Error sending email:', ex)
      }
    },

    handleSupplierStatus(eo) {
      this.checkIfZipMatches()
      var eoNumber = eo.data
      if (!eoNumber) {
        this.isEvedSupplier = false
      } else {
        this.isEvedSupplier = true
      }
    },

    async validate(submit) {
      let validInvoice = true

      // Program Validation
      if (!this.invoiceInformationModel.ProgramID || this.invoiceInformationModel.ProgramID <= 0) {
        this.invoiceInformationModel.Errors.ProgramMissing = true
        validInvoice = false
      }

      // PO Validation
      if (this.isP2PInvoice && (!this.settingsModel.CompanySettings.AllowInvoiceToExceedPOQty ||
        !this.settingsModel.CompanySettings.ExcludeTaxesFromPOThreshold)) {
        if (!this.invoiceInformationModel.POid || this.invoiceInformationModel.POid <= 0) {
          this.invoiceInformationModel.Errors.POMissing = true
          validInvoice = false
        }

        // Invoice Amount Validation (it must be lower that PO notInvoicedAmount)
        let finalInvoiceAmount = this.getInvoiceAmountToValidateAgainstUninvoiced()
        let isPositiveAmount = this.invoiceInformationModel && Number(this.invoiceInformationModel.InvoiceAmount) >= 0
        if (finalInvoiceAmount && !isNaN(this.notInvoicedAmount) && (this.isCreateInvoice() || finalInvoiceAmount !== 0) &&
            (
              (isPositiveAmount && (finalInvoiceAmount > this.notInvoicedAmount || finalInvoiceAmount < 0)) ||
              (!isPositiveAmount && (finalInvoiceAmount < this.notInvoicedAmount || finalInvoiceAmount > 0))
            )) {
          if (this.isP2PInvoiceWithAddEditItems) {
            if (!this.settingsModel.CompanySettings.ExcludeTaxesFromPOThreshold) {
              this.invoiceInformationModel.Errors.InvoiceAmountExceededPOThreshold = true
              validInvoice = false
            }
          } else {
            this.invoiceInformationModel.Errors.InvoiceAmountOutOfRange = true
            validInvoice = false
          }
        }

        // validate qty, units and unit price min/max thresholds
        if (this.$refs.lineItemsComp && this.$refs.lineItemsComp.$refs.lineItems &&
          this.$refs.lineItemsComp.$refs.lineItems[0].validateCells()) {
          validInvoice = false
        }
      }

      // Supplier Validation
      if (!this.invoiceInformationModel.Supplier.SupplierName) {
        this.invoiceInformationModel.Errors.SupplierMissing = true
        validInvoice = false
      }

      // Payment Type Validation
      if ((this.invoiceInformationModel.PaymentTypeID <= 0 || !this.invoiceInformationModel.PaymentType) && submit) {
        this.invoiceInformationModel.Errors.PaymentTypeMissing = true
        validInvoice = false
      }

      // Invoice Date Validation
      if (!this.invoiceInformationModel.InvoiceDate && submit) {
        this.invoiceInformationModel.Errors.InvoiceDateMissing = true
        validInvoice = false
      }

      // Due Date Validation
      if (!this.invoiceInformationModel.DueDate && submit) {
        this.invoiceInformationModel.Errors.DueDateMissing = true
        validInvoice = false
      }

      // Attachments Validation
      if (this.settingsModel.CompanySettings.RequireAttachment && submit &&
        !this.hasAnyPDFAttachment()) {
        this.invoiceInformationModel.Errors.AttachmentsMissing = true
        validInvoice = false
      }

      // Supplier Zip
      if (this.invoiceInformationModel.IsRequiredSupplierZipCode && submit &&
        (!this.invoiceInformationModel.SupplierZipCode ||
        (this.invoiceInformationModel.SupplierZipCode || '').trim() === '')) {
        this.invoiceInformationModel.Errors.SupplierZipCodeMissing = true
        validInvoice = false
      }

      // Reference No
      if (this.$route.query.invoiceId !== '0' || !this.settingsModel.CompanySettings.AllowInvoiceScanning || this.isP2PInvoice) {
        if (this.invoiceInformationModel.ReferenceNoIsRequired && submit &&
            (!this.invoiceInformationModel.ReferenceNo ||
            (this.invoiceInformationModel.ReferenceNo || '').trim() === '')) {
          this.invoiceInformationModel.Errors.ReferenceNoMissing = true
          validInvoice = false
        }
      }

      // Currency Validation
      if (this.invoiceInformationModel.CurrencyID <= 0 || !this.invoiceInformationModel.CurrencyCode) {
        this.invoiceInformationModel.Errors.InvoiceCurrencyMissing = true
        validInvoice = false
      }

      if (this.useHighLevelAllocation || this.isP2PInvoice) {
        // Invoice Amount Validation
        if (!this.invoiceInformationModel.InvoiceAmount || Number(this.invoiceInformationModel.InvoiceAmount) === 0) {
          this.invoiceInformationModel.Errors.InvoiceAmountMissing = true
          validInvoice = false
        }
      }

       // Invoice scan - amount validation
      if (!this.useHighLevelAllocation && !this.isP2PInvoice && this.settingsModel.CompanySettings.AllowInvoiceScanning) {
        if (!this.settingsModel.CompanySettings.EditingInvoiceScan && !this.invoiceInformationModel.InitialTotalAmount || Number(this.invoiceInformationModel.InitialTotalAmount) === 0) {
          this.invoiceInformationModel.Errors.InitialTotalAmountMissing = true
          validInvoice = false
        }
      }

      // Approval Checkbox Validation
      if (!this.approvalCheckboxOptions[0].status && submit && this.settingsModel.CompanySettings.EditingInvoiceScan === false) {
        this.approvalCheckboxError = true
        validInvoice = false
      } else {
        this.approvalCheckboxError = false
      }

      // Approval Note
      if (this.isCreateInvoice() &&
        this.settingsModel.CompanySettings.EditingInvoiceScan === false &&
        !this.isApprovalNoteValid()) {
        this.approvalNoteError = true
        validInvoice = false
      } else {
        this.approvalNoteError = false
      }

      // Invoice matches PO Validation
      if (this.settingsModel.CompanySettings.RequireAdditionalInvoiceConfirmation && !this.additionalInvoiceConfirmationOptions[0].status && submit && this.settingsModel.CompanySettings.EditingInvoiceScan === false) {
        this.additionalInvoiceConfirmationError = true
        validInvoice = false
      } else {
        this.additionalInvoiceConfirmationError = false
      }

      // Line Items Validation
      if (!this.useHighLevelAllocation && !this.isP2PInvoice) {
        // i2p
        if (!this.settingsModel.CompanySettings.AllowInvoiceScanning &&
          this.purchaseOrder.PO.events && this.purchaseOrder.PO.events[0].lineItems.length === 0 && validInvoice === true) {
          let actionLabel = submit ? 'submit' : 'save'
          this.validationErrorTitle = 'Please add at least one line item to ' + actionLabel + ' your invoice.'
          validInvoice = false
        }

        // Line items names
        if (this.purchaseOrder.PO.events && this.purchaseOrder.PO.events[0].lineItems.filter(item => item._invalid === 'true').length > 0 && validInvoice === true) {
          this.validationErrorTitle = 'Please complete the empty fields for the highlighted items in the Line Items grid of the screen in order to submit your invoice.'
          validInvoice = false
        }
      }

      if (!this.useHighLevelAllocation && this.isP2PInvoice) {
        if (this.purchaseOrder.PO.events[0].lineItems.some(i => i.entireRowInvalid)) {
          this.validationErrorTitle = 'The PO does not have the Processing Fee Item, please edit and resubmit the PO before submitting the invoice.'
          validInvoice = false
        }
      }

      // High Level Validation
      if (this.programId > 0 && this.useHighLevelAllocation && !this.isP2PInvoice) {
        let validationMessageModel = this.validateHighLevelAllocations()
        if (validationMessageModel && validationMessageModel.ErrorMessage !== '') {
          this.validationErrorTitle = validationMessageModel.ErrorMessage
          this.validationTitle = validationMessageModel.ErrorTitle
          validInvoice = false
        }
      }

      // Same day invoice
      if (this.invoiceInformationModel && this.invoiceInformationModel.IsSameDayDeposit && submit) {
        // negative amount validation
        let invoiceAmount = !this.isP2PInvoice
                          ? Number(this.purchaseOrder.PO.events[0].total) // i2p
                          : Number(this.invoiceInformationModel.InvoiceAmount)
        if (invoiceAmount < 0) {
          this.invoiceInformationModel.Errors.InvoiceAmountNegative = true
          validInvoice = false
        }

        // Same day deposit - thresholds validation
        let validationMessageModel = await this.validateSameDayThresholds()
        if (validationMessageModel && validationMessageModel.ErrorMessage !== '') {
          this.validationErrorTitle = validationMessageModel.ErrorMessage
          this.validationTitle = validationMessageModel.ErrorTitle
          validInvoice = false
        }
      }

      if (validInvoice && submit && this.settingsModel.CompanySettings.EditingInvoiceScan && !this.validateInvoiceAmount()) {
        validInvoice = false
      }

      // Allocation type for scan validation
      if (this.invoiceInformationModel && this.invoiceInformationModel.HasAllocationSection && !this.invoiceInformationModel.IsAllocationSelected) {
        this.invoiceInformationModel.Errors.AllocationTypeMissing = true
        validInvoice = false
      }

      // Allocation for scan validation
      if (this.invalidAllocationForScan()) {
        this.invoiceInformationModel.Errors.AllocationMissing = true
        validInvoice = false
      }

      // P2P with line items validations
      if (validInvoice && submit && this.isP2PInvoiceWithAddEditItems) {
        if (!this.hasAtleastOneItemAdded()) {
          this.validationErrorTitle = 'This PO was already invoiced in full.'
          this.validationTitle = ''
          validInvoice = false
        }

        if (validInvoice && this.settingsModel.CompanySettings.AllowInvoiceToExceedPOQty &&
          !this.isValidItemTotalAmount()) {
          // validate invoiced amount
          validInvoice = false
        }

        // IMPORTANT: this must be the last validation
        if (validInvoice && this.hasItemsWithUnitPriceAtZero()) {
          if (this.modals.allowItemsWithZeroUnitPrice) {
            this.showUnitPriceAtZeroWarning = true
            validInvoice = false
          }
        }
      }

      // Custom Fields
      if (!(this.$refs.invoiceInformationRef.$refs.customFieldsRef &&
        this.$refs.invoiceInformationRef.$refs.customFieldsRef.validate())) {
        validInvoice = false
      }

      // display error
      if (this.validationErrorTitle) {
        this.showValidationError = true
      }

      return validInvoice
    },

    isValidItemTotalAmount() {
      let result = true
      if (this.isP2PInvoiceWithAddEditItems) {
        let excludeTaxes = this.settingsModel.CompanySettings.ExcludeTaxesFromPOThreshold
        this.purchaseOrder.PO.events[0].lineItems.forEach(function(item) {
          if (!item.isAutoGenerated && Number(item.quantity) !== 0) {
            let amountToValidate = excludeTaxes
              ? item.total // allow any amount for taxes
              : item.totalWithTaxesAndFees

            // total amount validation
            if (Number(amountToValidate) < Number(item.minTotalAmount) ||
              Number(amountToValidate) > Number(item.maxTotalAmount)) {
              item._invalid = true
              result = false
            }

            // unit price validation
            if ((!item.isFeeDiscountItem || Number(item.amountTypeId) === 1) &&
              Number(item.unitPrice) > Number(item.maxUnitPrice)) {
              item._invalid = true
              result = false
            }
          }
        })
      }
      return result
    },

    hasItemsWithUnitPriceAtZero() {
      let result = false
      if (this.isP2PInvoiceWithAddEditItems) {
        this.purchaseOrder.PO.events[0].lineItems.forEach(function(item) {
          if (!item.isQuantityReadonly && (
            (item.isFeeDiscountItem && Number(item.amountTypeValue) === 0) ||
            (!item.isFeeDiscountItem && Number(item.unitPrice) === 0))) {
            result = true
          }
        })
      }
      return result
    },

    hasAtleastOneItemAdded() {
      // avoid submitting the invoice with items without quantity
      let result = false
      if (this.isP2PInvoiceWithAddEditItems) {
        this.purchaseOrder.PO.events[0].lineItems.forEach(function(item) {
          if (Number(item.quantity) !== 0) {
            result = true
          }
        })
      }
      return result
    },

    validateHighLevelAllocations() {
      let validationMessageModel = {
        ErrorMessage: '',
        ErrorTitle: ''
      }
      // Allocations 100%
      if (!this.validateAllocationsTotal()) {
        validationMessageModel.ErrorMessage = 'Please allocate your invoice to 100% in the Allocation sections of the screen to submit your invoice.'
      }

      if (!this.validateAllocations()) {
        validationMessageModel.ErrorMessage = 'Please select a value from the drop-down or complete the missing fields ' +
        'for the highlighted items in the Allocation sections of the screen in order to submit your invoice.'
      }
      return validationMessageModel
    },

    async validateSameDayThresholds() {
      let invoiceAmount = !this.isP2PInvoice
                          ? Number(this.purchaseOrder.PO.events[0].total) // i2p
                          : Number(this.invoiceInformationModel.InvoiceAmount)
      if (invoiceAmount === 0) {
        invoiceAmount = this.invoiceInformationModel.InitialTotalAmount
      }

      let validationMessageModel = {
        ErrorMessage: '',
        ErrorTitle: 'Amount Exceeds Threshold'
      }
      await this.api.get(this.api.$.invoice.i2p.getImmediatePaymentSettings, {
        params: {
          divisionId: this.invoiceInformationModel.DivisionID,
          currencyCode: this.invoiceInformationModel.CurrencyCode,
          invoiceIdEdited: this.invoiceInformationModel.PaymentInvoiceId,
          invoiceAmount: invoiceAmount
        }
      }).then(response => {
        if (response.data.NoCurrency) {
          validationMessageModel.ErrorMessage = 'There are no escrow funds for currency ' + this.invoiceInformationModel.CurrencyCode + '.'
          return validationMessageModel
        }

        if (response.data.InvoiceAmountInDefaultCurrency) {
          invoiceAmount = response.data.InvoiceAmountInDefaultCurrency
        }

        if (response.data.TransactionLimit !== 0 && response.data.TransactionLimit < invoiceAmount) {
          let formattedLimit = formatter.getMoneyFormatedValue(response.data.TransactionLimit, response.data.EscrowCurrencyCode)
          validationMessageModel.ErrorMessage = 'This invoice amount exceeds the allowed amount per transaction of ' +
                                  formattedLimit + '. Please adjust your invoice amount to be below the threshold amount.'
          return validationMessageModel
        }
        if (response.data.TotalLimit !== 0 && response.data.Balance < invoiceAmount) {
          let formattedAmount = formatter.getMoneyFormatedValue((invoiceAmount - response.data.Balance), response.data.EscrowCurrencyCode)
          let formattedTotalLimit = formatter.getMoneyFormatedValue(response.data.TotalLimit, response.data.EscrowCurrencyCode)
          validationMessageModel.ErrorMessage = 'This invoice will push the total balance allowed for your company (' + formattedTotalLimit + '). ' +
            'In order to push this as a ' + utils.constants.invoice.SameDayLabel + ' payment invoice, please reduce your total amount by ' + formattedAmount + '.'
          return validationMessageModel
        }
      })
      return validationMessageModel
    },

    validateInvoiceAmount() {
      // validate total amount excluding auto-generated merchant fee and discounted payment term (i2p)
      let autogeneratedAmounts = this.getAutogeneratedAmount()

      let _initialAmount = (this.invoiceInformationModel.InitialTotalAmount + autogeneratedAmounts).toFixed(2)
      if (+this.purchaseOrder.PO.events[0].total !== +_initialAmount) {
        if (!this.modals.allowDifferentAmount) {
          this.showDifferentAmountWarning = true
          return false
        }
      }
      return true
    },

    getAutogeneratedAmount() {
      // i2p
      let sum = 0
      this.purchaseOrder.PO.events && this.purchaseOrder.PO.events[0] && this.purchaseOrder.PO.events[0].lineItems &&
      this.purchaseOrder.PO.events[0].lineItems.forEach(function(item) {
        if (item.isAutoGenerated) {
          sum += Number(item.total)
        }
      })
      return sum
    },

    invalidAllocationForScan() {
      if (!this.invoiceInformationModel.HasAllocationForScan) {
        this.costCentersForScan = []
        this.pairsForScan = []
      }
      return this.invoiceInformationModel && this.invoiceInformationModel.HasAllocationSection &&
        this.invoiceInformationModel.IsAllocationSelected &&
        this.invoiceInformationModel.HasAllocationForScan &&
        (this.costCentersForScan === null ||
        this.costCentersForScan && this.costCentersForScan.length === 0) &&
        (this.pairsForScan === null ||
        this.pairsForScan && this.pairsForScan.length === 0)
    },

    loadedStep() {
      this.loading = false
    },

    highlightTabs() {
      this.$root.legacyTab = 'Commerce'
      this.$root.legacySubtab = 'Invoices'
    },

    clearTabs() {
      this.$nextTick(() => {
        this.$root.legacyTab = this.$root.legacySubtab = ''
        this.programId = 0
      })
    },

    resetJSONmodel() {
      this.invoiceInformationModel = utils.copy(invoiceInformationModel)
      this.purchaseOrder = utils.copy(purchaseOrder)
      this.settingsModel = utils.copy(settingsModel)
      this.budgetSnapshotModel = utils.copy(budgetSnapshotModel)
      this.budgetScanSnapshotModel = utils.copy(budgetScanSnapshotModel)
      this.modals = utils.copy(modals)

      // reset submission approval
      this.approvalCheckboxError = false
      this.approvalNoteError = false
      this.approvalCheckboxOptions[0].status = false

      this.additionalInvoiceConfirmationError = false
      this.additionalInvoiceConfirmationOptions[0].status = false
    },

    cancelInvoice() {
      if (this.batch.addAnotherInvoice) {
        this.createAnotherInvoice()
      } else {
        let tab = ''
        let subtab = ''
        let url = '/i/Programs/Invoice-received/'

        if (this.$route.query.from) {
          if (this.$route.query.from.toLowerCase().trim() === 'eoadmin') {
            url = '/d/InvoiceScan/queue/'
            // global.top.document.getElementById('VuePageFrame').className = 'VuePage'
            // global.top.document.body.style.overflow = 'auto'
            // return
          } else if (this.$route.query.from.toLowerCase().trim() === 'mydashboard') {
            url = '/d/Dashboard/MyDashboard/'
            tab = 'HomeEO'
            subtab = 'Dashboard'
          } else if (this.$route.query.from.toLowerCase().trim() === 'programinvoices' ||
            this.$route.query.from.toLowerCase().trim() === 'program-invoice-list') {
            url = '/i/Programs/Program-list/?goto=PaymentInvoice/ProgramInvoiceList/' +
              this.$route.query.programId
            tab = 'Commerce'
            subtab = 'Invoices'
          } else if (this.$route.query.from.toLowerCase().trim() === 'programdashboard') {
            url = '/i/Programs/Program-list/?goto=ProgramDashboard/ProgramDashboard/' +
              (typeof this.$route.query.programId !== 'undefined' ? this.$route.query.programId : 0)
            tab = 'Commerce'
            subtab = 'ProgramList'
          } else if (this.$route.query.from.toLowerCase().trim() === 'programsentpos') {
            // Program Dashboard > POs tab
            url = '/i/Programs/Program-list/?goto=QqSentPurchaseOrder/QqSentPurchaseOrder/' +
              (typeof this.$route.query.programId !== 'undefined' ? this.$route.query.programId : 0)
            tab = 'Commerce'
            subtab = 'ProgramList'
          } else if (this.$route.query.from.toLowerCase().trim().indexOf('poinvoices') === 0) {
            // PO Details > Invoices tab
            url = '/i/Programs/Program-list/?goto=PaymentInvoiceFusion' +
              (typeof this.$route.query.fusionPoId !== 'undefined' ? this.$route.query.fusionPoId : this.invoiceInformationModel.POid)
            tab = 'Commerce'
            subtab = 'ProgramList'
          } else if (this.$route.query.from.toLowerCase().trim() === 'sentpos') {
            // PO's grid
            url = '/i/Programs/PO-sent/?goto=QqSentPurchaseOrder'
            tab = 'Commerce'
            subtab = 'QqSentPurchaseOrder'
          } else if (this.$route.query.from.toLowerCase().trim().indexOf('podetails') === 0) {
            // PO Details
            url = '/i/Programs/PO-sent/?goto=/ScopeOfService/View/' + this.invoiceInformationModel.POMasterId + '/1'
            if (this.$route.query.from.toLowerCase().trim() === 'podetailsfromprogram') {
              url += '/' + this.$route.query.programId
            }
            tab = 'Commerce'
            subtab = 'QqSentPurchaseOrder'
          }
        } else if (this.$route.query.supplierId) {
          url = '/i/Network/SupplierNetwork/'
          tab = 'Network'
          subtab = 'Network'
        }

        if (tab !== '' && subtab !== '') {
          this.$root.legacyTab = tab
          this.$root.legacySubtab = subtab
        }
        this.$router.push(url)
      }
    },

    beforeCancelInvoice() {
      this.cancelClicked = true
      if (this.invoiceInformationModel.InvoiceIsSaved) {
        if (this.validate(false)) {
          this.cancelInvoice()
        }
      } else {
        this.cancelInvoice()
      }
    },

    createAnotherInvoice() {
      this.$router.push('/d/InvoiceToPay/upload?invoiceId=0&from=batch&programId=' + this.invoiceInformationModel.ProgramID)

      // reset data()
      let batchInvoices = this.batch.invoices
      Object.assign(this.$data, this.$options.data.call(this))

      this.batch.invoices = batchInvoices

      // call deactivated()
      this.onDeactivated()

      // call created()
      this.onCreated()
    },

    async submitAndAddAnother() {
      this.submitAfterConfirmation = true
      this.batch.addAnotherInvoice = true
      this.batch.submitAllInvoices = false
      this.doConfirmationsBeforeSave()
    },

    initSubmitBatch() {
      this.submitAfterConfirmation = true
      this.batch.addAnotherInvoice = false
      this.batch.submitAllInvoices = true
      this.doConfirmationsBeforeSave()
    },

    async saveOneInvoice(withSubmit) {
      this.submitAfterConfirmation = withSubmit
      this.batch.addAnotherInvoice = false
      this.batch.submitAllInvoices = false
      this.doConfirmationsBeforeSave()
    },

    async doConfirmationsBeforeSave() {
      let items = this.getInvoiceItems()
      let isValidBuAndCC = true

      isValidBuAndCC = !this.settingsModel.CompanySettings.UseBusinessUnitAndCostCenterMapping ||
        items.every(item => (item.businessUnitId && item.costCenterId && item.programBusinessUnitAndCostCenterId))

      if (!isValidBuAndCC) {
        if (this.isP2PInvoice) {
          alert('You cannot submit this invoice because line items are not allocated to BU&CCs. Please edit the PO, allocate all line items to BU&CCs, send the PO and then submit the invoice.')
          return false
        } else if (!this.useHighLevelAllocation && (items[0].costCenterAndBusinessUnit.costCenterAndBusinessUnitKey === null || items[0].costCenterAndBusinessUnit.costCenterAndBusinessUnitKey === '')) {
          alert('You cannot submit this invoice because line items are not allocated to BU&CCs.')
          return false
        }
      }

      this.verifySupplierCountry()
      return true
    },

    fetchCountries() {
      this.api.forms.post(
        this.api.$.settings.company.countries,
        {
          q: ''
        }
      ).then(r => {
        if (r && r.data && r.data.Data) {
          this.countries = r.data.Data
        }
      }).catch(e => console.warn(e))
    },

    selectCountry() {
      this.showCountryMissing = false
    },

    onSupplierCountrySelected() {
      if (this.supplierCountry && this.supplierCountry.Value) {
        this.purchaseOrder.PO.supplier.address.country = this.supplierCountry.Value
        this.modals.selectSupplierCountry = false
        this.verifySupplierCountry()
      } else {
        this.showCountryMissing = true
      }
    },

    closeSupplierCountrySelection() {
      this.modals.selectSupplierCountry = false
      this.submitAfterConfirmation = false
    },

    supplierWasChanged() {
      return +this.purchaseOrderInitialModel.supplier.vci !== +this.purchaseOrder.PO.supplier.vci ||
        this.purchaseOrderInitialModel.supplier.email !== this.purchaseOrder.PO.supplier.email
    },

    verifyBudgetAlmostUsed() {
      let canSave = true
      if (!this.settingsModel.CompanySettings.EditingInvoiceScan) {
        if ((this.budgetSnapshotModel.BudgetBalance * 100 / this.budgetSnapshotModel.ProgramBudget) < 20 &&
          (this.budgetSnapshotModel.BudgetBalance * 100 / this.budgetSnapshotModel.ProgramBudget) >= 0) {
          this.showBudgetAlmostUsedNotification = true
          canSave = false
        }
      }

      if (canSave) {
        this.save(this.submitAfterConfirmation)
      }
    },

    async verifySupplierCountry() {
      // verify supplier provide services in the Bill To country
      let verifySupplier = this.submitAfterConfirmation &&
        typeof this.purchaseOrder.PO.supplier.address !== 'undefined' && // supplier selected
        typeof this.purchaseOrder.PO.billTo.address !== 'undefined' &&
        (this.isCreateInvoice() || this.supplierWasChanged() ||
        (this.invoiceInformationModel.IsDraft && !this.settingsModel.CompanySettings.EditingInvoiceScan))

      if (verifySupplier) {
        if ((this.purchaseOrder.PO.supplier.address.country || '').trim() === '') {
          if (this.purchaseOrder.PO.supplier.vci > 0) {
            this.api.get(this.api.$.invoice.i2p.getSupplierCountryName, {
              params: {
                supplierEONumber: this.purchaseOrder.PO.supplier.vci
              }
            })
            .then(r => {
              if (r && r.data) {
                this.purchaseOrder.PO.supplier.address.country = r.data
                this.compareSupplierAndBillToCountries()
              } else {
                this.showModalToChooseSupplierCountry()
              }
            })
          } else {
            this.showModalToChooseSupplierCountry()
          }
        } else {
          this.compareSupplierAndBillToCountries()
        }
      } else {
        EventBus.$emit(Eved.I2P.SUPPLIER_COUNTRY_CONFIRMED)
      }
    },

    showModalToChooseSupplierCountry() {
      this.supplierCountry = {}
      this.showCountryMissing = false
      this.modals.selectSupplierCountry = true
    },

    compareSupplierAndBillToCountries() {
      if (this.purchaseOrder.PO.billTo.address.country !== this.purchaseOrder.PO.supplier.address.country &&
        this.purchaseOrder.PO.billTo.address.country === 'United States' &&
        this.purchaseOrder.PO.supplier.withholdingTaxPercent !== 0
      ) {
        this.modals.showSupplierProvideServicesConfirmation = true
      } else {
        EventBus.$emit(Eved.I2P.SUPPLIER_COUNTRY_CONFIRMED)
      }
    },

    onSupplierCountryConfirmed() {
      this.verifyBudgetAlmostUsed()
    },

    closeBudgetAlmostUsedNotification() {
      this.showBudgetAlmostUsedNotification = false
      this.save(this.submitAfterConfirmation)
    },

    onSupplierProvideServicesConfirmation() {
      this.invoiceInformationModel.IsWithholdingTaxEligible =
        this.purchaseOrder.PO.supplier.address.country !== this.purchaseOrder.PO.billTo.address.country &&
        this.purchaseOrder.PO.billTo.address.country === 'United States'

      EventBus.$emit(Eved.I2P.SUPPLIER_COUNTRY_CONFIRMED)
    },

    closeSupplierProvideServicesConfirmation(data) {
      this.modals.showSupplierProvideServicesConfirmation = false
      if (data && data.btnClick === true) {
        EventBus.$emit(Eved.I2P.SUPPLIER_COUNTRY_CONFIRMED)
      }
    },

    async save(withSubmit) {
      if (this.batch.submitAllInvoices && (this.invoiceInformationModel.ProgramID || 0) <= 0) {
        this.modals.showSubmitBatchConfirmation = true
      } else {
        let valid = await this.validate(withSubmit)

        if (valid) {
          let supplierZipCodeIsDifferent = false
          if (!this.supplierZipCodeWarningWasAccepted && withSubmit) {
            supplierZipCodeIsDifferent = await this.checkSupplierZipCode()
          }
          if (!supplierZipCodeIsDifferent) {
            let isOverBudgetCase = this.isBeforeSendingToScan && !this.overBudgetWarningBeforeSendingToScanWasAccepted
            ? await this.isOverBudgetBeforeSendingToScan() : false
            if (!isOverBudgetCase) {
              this.savePurchaseOrder(this.submitAfterConfirmation)
              // after saving the PO, if required, then this.saveInvoice(withSubmit) will be called
            } else {
              this.showOverBudgetWarningBeforeSendingToScan = true
            }
          } else {
            this.showSupplierZipCodeWarning = true
          }
        }
      }
    },

    onSubmitBatchClose() {
      this.modals.showSubmitBatchConfirmation = false
      this.batch.submitAllInvoices = false
      this.batch.selectedInvoices = null
    },

    onSubmitBatchConfirmation() {
      // TODO: confirmation to skip validation on current invoice
      this.submitBatch()
    },

    getInvoiceIdsFromBatch() {
      if (this.batch.selectedInvoices === null ||
        this.batch.selectedInvoices.length === 0) return []

      let invoiceIds = []
      this.batch.invoices.map(inv => {
        if (this.batch.selectedInvoices.indexOf(inv.id) !== -1) {
          invoiceIds.push(inv.id)
        }
      })

      return invoiceIds
    },

    submitBatch() {
      let invoiceIds = this.getInvoiceIdsFromBatch()

      if (invoiceIds && invoiceIds.length > 0) {
        this.api.post(this.api.$.invoice.i2p.submitBatch, {
          invoiceIds: invoiceIds
        })
        .then(response => {
          this.batch = utils.copy(batch)

          this.onSubmitBatchClose()
          if (response.data.Success) {
            this.batch.invoices = []
            EventBus.$emit(Eved.I2P.VALIDATION_COMPLETED, { validInvoice: true, gotoNextTab: true })
          } else {
            // TODO: show error
          }
        })
      } else {
        this.batch.invoices = []
        EventBus.$emit(Eved.I2P.VALIDATION_COMPLETED, { validInvoice: true, gotoNextTab: true })
      }
    },

    onBatchInvoiceSelected(e) {
      if (e && Array.isArray(e)) {
        this.batch.selectedInvoices = []
        e.map(i => {
          if (i.data.id) {
            this.batch.selectedInvoices.push(i.data.id)
          }
        })
      } else {
        this.batch.selectedInvoices = []
      }
    },

    closeValidationError() {
      this.showValidationError = false
      this.validationErrorTitle = ''
      this.validationTitle = ''
    },

    savePurchaseOrder(submit) {
      this.saveInvoice(submit)

      // IMPORTANT: uncomment after removing from backend the save/submit/get of the PO
      /* if (!this.isP2PInvoice) {
        let url = this.invoiceInformationModel.UseSavingsFields
          ? api.SaveSavings()
          : api.SavePurchaseOrder()

        // save PO
        this.api.post(
            url, { params: {
              userId: this.user.buyerUserID,
              data: this.purchaseOrder.PO
            }}
          ).then(r => {
            if (r && r.data) {
              this.invoiceInformationModel.PONumber = r.data

              // send PO to supplier
              if (submit) {
                url = this.invoiceInformationModel.UseSavingsFields
                  ? api.SendSavings()
                  : api.SendPurchaseOrder()

                this.api.post(
                  url, { params: {
                    userId: this.user.buyerUserID,
                    data: this.purchaseOrder.PO
                  }}
                ).then(r => {
                  if (r && r.data) {
                    // save/submit invoice
                    this.invoiceInformationModel.PONumber = r.data
                    this.saveInvoice(submit)
                  }
                })
                .catch(e => this.onSaveError())
              } else {
                // save/submit invoice
                this.saveInvoice(submit)
              }
            }
          })
          .catch(e => this.onSaveError())
      } else {
        // p2p: save/submit invoice
        this.saveInvoice(submit)
      } */
    },

    setInvoiceAmount() {
      if (!this.isP2PInvoice) {
        this.invoiceInformationModel.InvoiceAmount = this.purchaseOrder.PO.events[0].total // i2p
        if (!this.isBeforeSendingToScan) {
          this.invoiceInformationModel.VATGSTAmount = +this.purchaseOrder.PO.vat + +this.purchaseOrder.PO.gst
        }
      }
    },

    setInvoiceDates() {
      if (this.invoiceInformationModel.InvoiceDate) {
        this.invoiceInformationModel.InvoiceDate = global.moment(this.invoiceInformationModel.InvoiceDate).format('MM/DD/YYYY')
      }
      if (this.invoiceInformationModel.DueDate) {
        this.invoiceInformationModel.DueDate = global.moment(this.invoiceInformationModel.DueDate).format('MM/DD/YYYY')
      }
    },

    getInvoiceItems() {
      let allItems = []
      this.purchaseOrder.PO.events.forEach(ev => {
        ev.lineItems.forEach(itm => {
          let newItem = itm
          newItem.id = itm.itemKey
          newItem.invoiceItemKey = itm.invoiceItemKey
          newItem.eventKey = ev.uniqueId
          newItem.categoryName = itm.category
          newItem.categoryRefNo = itm.referenceId
          newItem.costCenterNo = (itm.costCenterAndBusinessUnit && itm.costCenterAndBusinessUnit.costCenterNo) || ''
          newItem.costCenterName = (itm.costCenterAndBusinessUnit && itm.costCenterAndBusinessUnit.costCenterName) || ''
          newItem.businessUnitName = (itm.costCenterAndBusinessUnit && itm.costCenterAndBusinessUnit.businessUnitName) || ''
          newItem.programBusinessUnitAndCostCenterId = (itm.costCenterAndBusinessUnit && itm.costCenterAndBusinessUnit.programBusinessUnitAndCostCenterId) || null
          newItem.businessUnitId = (itm.costCenterAndBusinessUnit && itm.costCenterAndBusinessUnit.businessUnitId) || null
          newItem.costCenterId = (itm.costCenterAndBusinessUnit && itm.costCenterAndBusinessUnit.costCenterId) || null
          allItems.push(newItem)
        })
      })
      return allItems
    },

    // IMPORTANT: should be called only from savePurchaseOrder method
    saveInvoice(submit) {
      this.setInvoiceAmount()
      this.setInvoiceDates()

      submit = submit || false

      this.submitDisabled = true

      let items = this.getInvoiceItems()

      this.invoiceInformationModel.CustomFields =
        this.$refs.invoiceInformationRef.$refs.customFieldsRef.getFields(true, true)

      // call SaveBudget to save purchase order details
      let model = {
        purchaseOrder: JSON.stringify(this.purchaseOrder.PO),
        items,
        invoice: this.invoiceInformationModel,
        supplier: this.invoiceInformationModel.Supplier,
        submit: submit,
        allowDuplicateInvoices: this.allowDuplicateInvoices,
        costCenters: this.ccAllocations && this.ccAllocations.AllocationList,
        buAndCcPairs: this.pairsAllocations && this.pairsAllocations.AllocationList,
        categories: this.categoriesAllocations && this.categoriesAllocations.AllocationList,
        costCentersForScan: this.costCentersForScan,
        buAndCcPairsForScan: this.pairsForScan,
        submitBatch: this.batch.addAnotherInvoice || this.batch.submitAllInvoices
      }

      this.api.post(this.api.$.invoice.i2p.saveInvoice, model)
        .then(response => {
          if (this.allowDuplicateInvoices) this.resetDuplicateInvoice()
          this.submitDisabled = false

          if (response.data && response.data.SuccessMessage === 'Error') {
            this.showSaveInvoiceError()
            this.submitDisabled = true
          } else if (response.data && response.data.DuplicateInvoices &&
            response.data.DuplicateInvoices.length > 0) { // Duplicate Invoice
            this.showDuplicateInvoice(response.data.DuplicateInvoices, submit)
          } else {
            let data = response.data
            let isOverBudget = data && data.IsOverBudget
            let isPendingScanning = data && data.IsPendingScanning

            // reload purchase order model
            // IMPORTANT: uncomment after removing from backend the save/submit/get of the PO
            /* if (!this.isP2PInvoice && (!submit || isOverBudget || isPendingScanning ||
              this.batch.submitAllInvoices)) {
              this.getPurchaseOrder().then(r => {
                if (r && r.data) { */
            if (data && data.PurchaseOrder &&
              /* if the page will change, no need to reload the PO */
              (isOverBudget || !(submit || this.batch.submitAllInvoices || this.batch.addAnotherInvoice))) {
              let responsePO = JSON.parse(data.PurchaseOrder) // r.data
              responsePO.events = this.store().keepItemCustomFields(responsePO.events, this.purchaseOrder.PO.events)

              this.purchaseOrder.PO = responsePO
              this.purchaseOrder.PO.events = this.store().getEventsWithOrderedLineItems(this.purchaseOrder.PO.events)

              if (this.purchaseOrder.PO.currency && this.purchaseOrder.PO.currency.code) {
                this.purchaseOrder.PO.currencySelected = true
                this.currencyCode = this.purchaseOrder.PO.currency.code
              }
              // update the grid component with the changed model
              this.$nextTick(() => {
                this.recalculatePO()
              })
            }
              /* })
            } */

            this.$nextTick(() => {
              let oldInvoiceId = this.invoiceInformationModel.PaymentInvoiceId
              if (data.PaymentInvoiceId) {
                this.invoiceInformationModel.PaymentInvoiceId = data.PaymentInvoiceId
                this.invoiceInformationModel.InvoiceIsSaved = true
              } else {
                this.showSaveInvoiceError()
              }

              let isBatchAction = this.batch.addAnotherInvoice || this.batch.submitAllInvoices
              if (!isOverBudget && isBatchAction) {
                this.addOrUpdateInvoiceToBatch(oldInvoiceId)
              }

              this.cacheInitialData()

              if (isOverBudget) {
                this.submitWasPressed = submit
                this.showOverBudget(data.FusionVersionId)
              } else if (isPendingScanning && !isBatchAction) {
                this.submitWasPressed = submit
                this.showPendingScanningWarning = true
              } else {
                if (this.batch.submitAllInvoices) {
                  // show Submit Batch popup after Submit
                  this.modals.showSubmitBatchConfirmation = true
                } else {
                  EventBus.$emit(Eved.I2P.VALIDATION_COMPLETED, { validInvoice: true, gotoNextTab: submit })
                }
              }
            })
          }
        })
    },

    getPurchaseOrder(poNumber, userId) {
      if (!poNumber) poNumber = this.invoiceInformationModel.PONumber
      if (!userId) userId = this.user.buyerUserID

      let url = this.invoiceInformationModel.UseSavingsFields
      ? api.GetSavingsDetails()
      : api.GetPurchaseOrderDetails()

      return this.api.get(
        url + '?uniqueId=' + poNumber + '&userId=' + userId
      )
      .catch(e => this.onSaveError())
    },

    showSaveInvoiceError() {
      global.app.$notifications.notify({
        alert: true,
        type: 'plain',
        icon: 'exclamation-circle',
        placement: { from: 'top', align: 'center' },
        timeout: 0,
        message: 'Something went wrong. Please resubmit the invoice.'
      })
    },

    showOverBudgetWarningBeforeSendingToScan() {
      global.app.$notifications.notify({
        alert: true,
        type: 'plain',
        icon: 'exclamation-circle',
        placement: { from: 'top', align: 'center' },
        timeout: 0,
        message: 'showOverBudgetWarningBeforeSendingToScan.'
      })
    },

    onAcceptOverBudgetWarningBeforeSendingToScan() {
      this.showOverBudgetWarningBeforeSendingToScan = false
      this.overBudgetWarningBeforeSendingToScanWasAccepted = true
      this.save(this.submitAfterConfirmation)
    },

    onAcceptSupplierZipCodeWarning() {
      this.showSupplierZipCodeWarning = false
      this.supplierZipCodeWarningWasAccepted = true
      this.save(this.submitAfterConfirmation)
    },

    addOrUpdateInvoiceToBatch(oldInvoiceId) {
      if (!this.batch.invoices) this.batch.invoices = []

      let invmodel = {
        id: this.invoiceInformationModel.PaymentInvoiceId,
        programName: this.invoiceInformationModel.ProgramName,
        divisionName: this.invoiceInformationModel.DivisionName,
        supplierCompanyName: this.purchaseOrder && this.purchaseOrder.PO && this.purchaseOrder.PO.supplier
          ? this.purchaseOrder.PO.supplier.companyName
          : this.invoiceInformationModel.SupplierName,
        supplierInvoiceNumber: this.invoiceInformationModel.ReferenceNo,
        totalAmount: this.settingsModel.CompanySettings.AllowInvoiceScanning && !this.isP2PInvoice
          ? this.invoiceInformationModel.InitialTotalAmount
          : this.invoiceInformationModel.InvoiceAmount,
        currencyCode: this.invoiceInformationModel.CurrencyCode
      }

      let canAdd = true
      this.batch.invoices.map(inv => {
        if (this.invoiceInformationModel.PaymentInvoiceId === inv.id ||
          oldInvoiceId === inv.id) {
          inv.id = invmodel.id
          inv.programName = invmodel.programName
          inv.divisionName = invmodel.divisionName
          inv.supplierCompanyName = invmodel.supplierCompanyName
          inv.supplierInvoiceNumber = invmodel.supplierInvoiceNumber
          inv.totalAmount = invmodel.totalAmount
          inv.currencyCode = invmodel.currencyCode

          canAdd = false
        }
      })

      if (canAdd) {
        this.batch.invoices.push(invmodel)
      }
    },

    showOverBudget(fusionVersionId) {
      this.api.get(api.GetOverBudgetWarningData(), {
        params: {
          fusionVersionID: fusionVersionId,
          context: 'Invoice',
          invoiceId: this.$route.query.invoiceId
        }
      })
        .then(response => {
          this.overBudgetModel = response.data
          this.showOverBudgetWarning = true
        })
    },

    confirmOverBudget() {
      this.showOverBudgetWarning = false
      if (this.batch.submitAllInvoices) {
        this.onSubmitBatchConfirmation()
      } else {
        EventBus.$emit(Eved.I2P.VALIDATION_COMPLETED, { validInvoice: true, gotoNextTab: this.submitWasPressed })
      }
    },

    doCancelInvoiceAction() {
      this.showOverBudgetWarning = false
      let paymentInvoiceId = this.invoiceInformationModel.PaymentInvoiceId
      this.api.post(this.api.$.invoice.i2p.cancelInvoice, { paymentInvoiceId: paymentInvoiceId })
      .then(r => {
        EventBus.$emit(Eved.I2P.VALIDATION_COMPLETED, { validInvoice: true, gotoNextTab: this.submitWasPressed })
      })
    },

    closePendingScanningModal() {
      this.showPendingScanningWarning = false
      EventBus.$emit(Eved.I2P.VALIDATION_COMPLETED, { validInvoice: true, gotoNextTab: this.submitWasPressed })
    },

    showDuplicateInvoice(duplicateInvoices, submit) {
      this.resetDuplicateInvoice()

      this.duplicateInvoices = duplicateInvoices
      this.allowDuplicateInvoices = false
      this.submitWasPressed = submit
      this.showDuplicateInvoiceStep1 = true
    },

    duplicateInvoiceAction() {
      this.showDuplicateInvoiceStep2 = false
      this.allowDuplicateInvoices = true
      if (this.batch.submitAllInvoices) {
        this.initSubmitBatch()
      } else {
        this.save(this.submitWasPressed)
      }
    },

    resetDuplicateInvoice() {
      this.duplicateInvoices = []
      this.showDuplicateInvoiceStep1 = false
      this.showDuplicateInvoiceStep2 = false
      this.allowDuplicateInvoices = false
    },

    duplicateInvoicePdfUrl(invoiceId) {
      return this.api.env + '/PaymentInvoice/ExportInvoice/?ids=[' + invoiceId + ']&isAllSelected=false'
    },

    initUploadInvoice() {
      return this.api
        .get(api.InitUploadInvoice(), {
          params: {
            paymentInvoiceId: this.$route.query.invoiceId,
            programId: this.$route.query.programId,
            supplierId: this.$route.query.supplierId,
            supplierVCI: this.$route.query.eoNumber,
            fusionPoId: Math.abs(this.$route.query.fusionPoId)
          }
        })
    },

    addSupplierData(e) {
      var supplierInfo = e.SupplierInfoVM.Supplier
      var supplierCountry = ''
      try {
        supplierCountry = this.purchaseOrder.PO.supplier.address.country
      } catch (e) {
        //
      }
      this.purchaseOrder.PO.supplier = {
        companyName: supplierInfo.SupplierName || '',
        network: null,
        phone: supplierInfo.SupplierPhone || '',
        address: {
          address1: supplierInfo.SupplierAddress1 || '',
          address2: supplierInfo.SupplierAddress2 || '',
          city: supplierInfo.SupplierCity || '',
          state: supplierInfo.SupplierState || '',
          region: supplierInfo.SupplierRegion || '',
          zipCode: supplierInfo.SupplierZIP || '',
          country: supplierInfo.CountryName || supplierCountry || ''
        },
        vci: e.Vci,
        id: supplierInfo.SupplierID || '',
        vatGstCode: supplierInfo.SupplierVatGstID || '',
        contactName: supplierInfo.SupplierContactName || '',
        email: supplierInfo.EmailAddress,
        useDifferentBillingAddress: 'False',
        withholdingTaxPercent: supplierInfo.WithholdingTaxPercent
      }

      if (e.SupplierInfoVM.SupplierId) {
        this.invoiceInformationModel.Supplier.VendorID = e.SupplierInfoVM.SupplierId
      }

      if (!this.isP2PInvoice) {
        // only for i2p invoices the supplier can be selected and contacts must be removed
        this.purchaseOrder.PO.supplierContacts = []
      }

      // run ComputeTotals to add or update auto generated taxes
      this.$nextTick(() => {
        if (!this.invoiceInformationModel.isEditInit) {
          this.recalculatePO()
        }
      })

      if (!e.IsInitSupplier) {
        this.api.forms.post(this.api.$.invoice.similarSuppliers, {
          supplierName: supplierInfo.SupplierName
        })
        .then(r => {
          if (r && r.data && r.data.SuccessMessage === 'Suppliers') {
            var suppliers = JSON.parse(r.data.Data)
            var alertMessage = 'There are multiple suppliers with a similar name to <b>' + supplierInfo.SupplierName + '</b> in the Eved Network:<br />'
            alertMessage += '<ul>'
            for (var i = 0; i < suppliers.length; i++) {
              var supplierInformation = suppliers[i].split('|')
              alertMessage += '<li>' + supplierInformation[0] + '<ul>'
              alertMessage += '<li> Business Type: ' + supplierInformation[1] + '</li>'
              alertMessage += '<li> Location: ' + supplierInformation[2] + '</li>'
              alertMessage += '</ul></li>'
            }
            alertMessage += '</ul>'
            alertMessage += 'If you have not selected the correct supplier, please close this window and select the correct one.'
            global.app.$notifications.notify({message: alertMessage, width: 600, timeout: 0, placement: { from: 'top', align: 'center' }, type: 'warning', icon: 'exclamation-circle', dismiss: 'OK'})
          }
        })
        .catch(e => console.warn(e))
      }
    },

    onDiscountSelected(e) {
      this.purchaseOrder.PO.discountTerm = e.DiscountName

      this.invoiceInformationModel.DiscountName = e.DiscountName
      this.invoiceInformationModel.DiscountPaymentTermID = e.DiscountPaymentTermID

      // no need to recalculate PO when this action is called from supplier selection or PO selection
      // it will be triggered in another method
      if (!e.SkipRecalculatePO) {
        this.$nextTick(() => {
          if (!this.invoiceInformationModel.isEditInit) {
            this.recalculatePO()
          }
        })
      }
    },

    // PO selected in combo
    onUpdatePOModel(e) {
      this.purchaseOrder.PO = e.PO

      this.invoiceInformationModel.POMasterId = e.POMasterId

      if (this.purchaseOrder.PO.additionalInvoiceConfirmationText) {
        this.additionalInvoiceConfirmationOptions = [
          {
            status: false,
            label: this.purchaseOrder.PO.additionalInvoiceConfirmationText
          }
        ]
      }

      if (this.isP2PInvoiceWithAddEditItems) {
        this.getRemainingQtyAndAmountOnPOItems(e.POMasterID)
      } else {
        this.$nextTick(() => {
          EventBus.$emit(Events.I2P.UPDATE_PO_MODEL_COMPLETED)
          if (!this.invoiceInformationModel.isEditInit) {
            this.recalculatePO()
          }
        })
      }
    },

    getRemainingQtyAndAmountOnPOItems(poMasterId) {
      if (this.isP2PInvoiceWithAddEditItems) {
        this.api.forms.post(this.api.$.invoice.i2p.getRemainingQtyAndAmountOnPOItems, {
          poMasterId: poMasterId
        })
        .then(r => {
          if (r && r.data && r.data.Data) {
            let items = JSON.parse(r.data.Data)

            if (this.isP2PInvoiceWithAddEditItems) {
              this.purchaseOrder.PO.events = this.updateQtyAndUnitsFromPOonCreate(this.purchaseOrder.PO.events, items)
            } else {
              this.purchaseOrder.PO.events = items
            }

            EventBus.$emit(Events.I2P.UPDATE_PO_MODEL_COMPLETED)
            this.recalculatePO()
          }
        })
        .catch(e => console.warn(e))
      }
    },

    onCurrencySelected(e) {
      this.currencyCode = e.code
      this.purchaseOrder.PO.currency.code = e.code
      this.purchaseOrder.PO.currency.symbol = e.symbol
      this.purchaseOrder.PO.currency.decimalPlaces = e.decimalPlaces
      this.purchaseOrder.PO.currencySelected = e.code && e.code !== ''

      this.budgetSnapshotModel.OriginalInvoiceCurrencyCode = this.budgetSnapshotModel.InvoiceCurrencyCode
      this.budgetSnapshotModel.InvoiceCurrencyCode = e.code

      if (!this.isP2PInvoice) {
        if (this.useHighLevelAllocation || (!this.settingsModel.CompanySettings.EditingInvoiceScan && this.settingsModel.CompanySettings.AllowInvoiceScanning)) {
          this.updateSnaphot()
        } else {
          this.updateLineItemSnaphot()
        }
      }
      if (this.settingsModel.CompanySettings.EditingInvoiceScan) {
        this.updateInvoiceScanSnapshot()
      }
    },
    onChangeSameDaySetting(e) {
      this.invoiceInformationModel.IsSameDayDeposit = e.status
    },
    onChangeReimbursementSetting(e) {
      this.invoiceInformationModel.IsReimbursement = e.status
    },
    updateLineItemSnaphot() {
      EventBus.$emit(Events.I2P.UPDATE_SNAPSHOT, {
        total: +this.purchaseOrder.PO.total,
        totalVATGST: +this.purchaseOrder.PO.vat + +this.purchaseOrder.PO.gst,
        programCurrencyCode: this.purchaseOrder.PO.programDetails.currencyCode
      })
    },
    updateInvoiceScanSnapshot() {
      EventBus.$emit(Events.I2P.UPDATE_INVOICE_SCAN_SNAPSHOT, {
        total: +this.purchaseOrder.PO.total,
        invoiceCurrencyCode: this.invoiceInformationModel.CurrencyCode
      })
    },

    removeSupplierData(e) {
      this.purchaseOrder.PO.supplier = {}
      this.purchaseOrder.PO.supplierContacts = []

      this.invoiceInformationModel.SupplierId = 0
      this.invoiceInformationModel.SupplierName = ''
      this.invoiceInformationModel.SupplierUserId = 0
      this.invoiceInformationModel.Supplier = {
        VendorID: 0,
        VendorUserID: 0
      }
    },

    validateProgramSelected(e) {
      let valid = this.purchaseOrder.PO.events && this.purchaseOrder.PO.events[0].lineItems.length === 0 // i2p

      if (valid) {
        this.resetScanningAllocation()
      }

      EventBus.$emit(Eved.I2P.CONFIRM_PROGRAM_SELECTED, {
        canSelectProgram: valid,
        program: e
      })
    },

    resetScanningAllocation() {
      if (this.settingsModel.CompanySettings.AllowInvoiceScanning &&
        this.paymentInvoiceId <= 0) {
        this.costCentersForScan = null
        this.invoiceInformationModel.HasAllocationForScan = false
      }
    },

    updateAllocationModel(type, updatedModel, validAllocations) {
      switch (type) {
        case 'CostCenters':
          this.ccAllocations.AllocationList = updatedModel
          this.ccAllocations.WasReset = true
          this.pairsAllocations.WasReset = false
          this.ccAllocations.ValidAllocations = validAllocations
          break
        case 'Pairs':
          this.pairsAllocations.AllocationList = updatedModel
          this.pairsAllocations.WasReset = true
          this.ccAllocations.WasReset = false
          this.pairsAllocations.ValidAllocations = validAllocations
          break
        case 'Categories':
          this.categoriesAllocations.AllocationList = updatedModel
          this.categoriesAllocations.ValidAllocations = validAllocations
          break
      }
    },
    updateAllocationForScan(e) {
      this.costCentersForScan = e.data && e.data.costCenters.map(i => {
        var cc = {
          Id: i.key,
          Name: i.value
        }
        return cc
      })

      this.pairsForScan = e.data && e.data.pairs.map(i => {
        var pair = {
          Id: i.key,
          Name: i.value
        }
        return pair
      })
      this.invoiceInformationModel.Errors.AllocationMissing = false
    },

    mapAllocation() {
      let allocationParameter = {}
      allocationParameter.CostCenters = this.ccAllocations.AllocationList
      allocationParameter.BuAndCcPairs = this.pairsAllocations.AllocationList
      allocationParameter.Categories = this.categoriesAllocations.AllocationList
      allocationParameter.InvoiceAmount = this.invoiceInformationModel.InvoiceAmount
      allocationParameter.VATAmount = this.invoiceInformationModel.VATGSTAmount
      allocationParameter.ProgramId = this.invoiceInformationModel.ProgramID

      return allocationParameter
    },

    checkApprovalCheckBox(e) {
      if (this.approvalCheckboxOptions.status && this.approvalCheckboxOptions.status === true) {
        this.approvalCheckboxError = false
      }
    },

    checkAdditionalInvoiceConfirmationBox(e) {
      if (this.additionalInvoiceConfirmationOptions.status && this.additionalInvoiceConfirmationOptions.status === true) {
        this.additionalInvoiceConfirmationError = false
      }
    },

    updatePOItemsAfterAllocationChange() {
      let allocationParameter = this.mapAllocation()

      this.allocationsStore().getPurchaseOrderItems(
        allocationParameter
        ).then(response => {
          if (response.Data) {
            this.purchaseOrder.PO.events[0].lineItems = JSON.parse(response.Data) // bu/cc allocation
            this.purchaseOrder.PO.events[0].total = this.invoiceInformationModel.InvoiceAmount

            this.purchaseOrder.PO.vat = 0
            this.purchaseOrder.PO.events[0].lineItems.map((item, index) => {
              if (item.amountTypeId === 1) {
                this.purchaseOrder.PO.vat += item.amountTypeValue
              }
            })

            this.$nextTick(() => {
              this.recalculatePO()
            })
          }
        })
    },

    async recalculatePO() {
      // Line Item Allocation
      if (this.useHighLevelAllocation === false) {
        EventBus.$emit(Eved.I2P.RECALCULATE_PO, {})
      } else {
        // High Level Allocation
        let eventsSectionData = new EventsSection(this.dependencies)
        this.purchaseOrder.PO = await eventsSectionData.recalculatePoAndReturnIt(
          this.purchaseOrder.PO, this.settingsModel.CompanySettings, this.isP2PInvoiceWithAddEditItems
        )
        this.purchaseOrder.PO.events = this.store().getEventsWithOrderedLineItems(this.purchaseOrder.PO.events)

        this.updateSnaphot()
      }
    },

    updateSnaphot() {
      if (!this.settingsModel.CompanySettings.EditingInvoiceScan && this.settingsModel.CompanySettings.AllowInvoiceScanning) {
        EventBus.$emit(Events.I2P.UPDATE_SNAPSHOT, {
          total: +this.invoiceInformationModel.InitialTotalAmount,
          totalVATGST: +this.VATAmount,
          programCurrencyCode: this.purchaseOrder.PO.programDetails.currencyCode,
          isP2PWorkflow: false
        })
      } else {
        EventBus.$emit(Events.I2P.UPDATE_SNAPSHOT, {
          total: +this.invoiceInformationModel.InvoiceAmount,
          totalVATGST: +this.invoiceInformationModel.VATGSTAmount,
          programCurrencyCode: this.purchaseOrder.PO.programDetails.currencyCode,
          isP2PWorkflow: this.isP2PInvoice
        })
      }
    },

    onCloseDifferentAmountModal() {
      this.showDifferentAmountWarning = false
    },

    onConfirmDifferentAmountModal() {
      this.showDifferentAmountWarning = false
      this.modals.allowDifferentAmount = true
      this.save(true)
    },

    onConfirmUnitPriceAtZero() {
      this.showUnitPriceAtZeroWarning = false
      this.modals.allowItemsWithZeroUnitPrice = true
      this.save(true)
    },

    onPOAmountChanged(e) {
      // this.notInvoicedAmount += e.total - e.previousTotal
    },

    isApprovalNoteValid() {
      return !this.settingsModel.CompanySettings.IsApprovalNoteRequired ||
        (
          (this.invoiceInformationModel.ApproveNote || '') !== '' &&
          this.invoiceInformationModel.ApproveNote !== this.settingsModel.CompanySettings.ApprovalNotePlaceholder
        )
    },

    onApprovalNoteChange() {
      if (this.approvalNoteError && this.isApprovalNoteValid()) {
        this.approvalNoteError = false
      }
    }
  }
}

attach()

</script>
<style scoped lang="scss">
  .cta.loading, .btn-actionbar.loading {
    pointer-events: none;
  }
</style>



// WEBPACK FOOTER //
// src/components/_modules/I2P/index.vue